import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useDropzone } from "react-dropzone";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { hideChildModal, hideModal, showChildModal } from "../../data/modal";
import { getSelectedTerminalCalls } from "../../data/selectedterminalcalls";
import { removeTerminalCall } from "../../data/terminalcall";
import {
  selectedFilteredTerminalCall,
  selectedFilteredVoyage,
} from "../../selectors/filterSelector";
import { normalizeIds } from "../utils/normalize";
import { runModalCallback } from "../../data/modalCallbacks";
import { getLabels } from "../../data/labels";
import { getTerminals } from "../../data/terminals";
import { getVessels } from "../../data/vessels";
import { getVoyages } from "../../data/voyage";
import {
  formatDateToString,
  getTerminalCallArrivalStateContainerIds,
  textColorHandler,
} from "../Helpers.tsx";
import { updateSelectedContainer } from "../../data/selectedcontainers";
import NotesPanel, {
  NoteItem,
  NoteValues,
} from "../notes/NotesPanel";
import AliasFields from "../Forms/Aliases";

import { MapContainer, Marker, Popup, TileLayer } from "react-leaflet";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  Add as AddIcon,
  CalendarMonth as CalendarMonthIcon,
  CancelOutlined as CancelOutlinedIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  DirectionsBoat as DirectionsBoatIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  FileOpen as FileOpenIcon,
  Folder as FolderIcon,
  Home as HomeIcon,
  Info as InfoIcon,
  InsertDriveFileSharp as InsertDriveFileSharpIcon,
  Link as LinkIcon,
  LocalShipping as LocalShippingIcon,
  Lock as LockIcon,
  Map as MapIcon,
  MoreVert as MoreVertIcon,
  Notes as NotesIcon,
  PlayArrow as PlayArrowIcon,
  Scale as ScaleIcon,
  Settings as SettingsIcon,
  South as UnloadingIcon,
  Stop as StopIcon,
  Terminal as TerminalIcon,
  UploadFileSharp as UploadFileSharpIcon,
  Image as ImageIcon,
  PictureAsPdf as PictureAsPdfIcon,
  North as LoadingIcon,
  Save as SaveIcon,
} from "@mui/icons-material";

type TerminalCallDetailsDashboardProps = {
  terminalCall?: any;
  containers?: any[];
  callback?: (data?: any) => Promise<void> | void;
  callbackId?: string;
  isChildModal?: boolean;
};

type OverviewStatsProps = {
  terminalCall: any;
  allContainers: any[];
  t: any;
};

const DEFAULT_TERMINAL_COLOR = "#FFE4C4";
const CARD_RADIUS = 2;
const SOFT_BORDER = "1px solid rgba(15, 23, 42, 0.1)";
const WRAPPER_BORDER_RADIUS = "10px";

const MODAL_FIXED_HEIGHT = "78vh";
const MODAL_FOOTER_HEIGHT = 48;

const UNLOADING_COLOR = "#16a34a";
const LOADING_COLOR = "#ca8a04";
const ONBOARD_COLOR = "#1565c0";

const UNLOADING_BACKGROUND =
  "linear-gradient(180deg, rgba(22,163,74,0.12) 0%, rgba(22,163,74,0.025) 100%)";

const LOADING_BACKGROUND =
  "linear-gradient(180deg, rgba(234,179,8,0.18) 0%, rgba(234,179,8,0.04) 100%)";

const ONBOARD_BACKGROUND =
  "linear-gradient(180deg, rgba(21,101,192,0.12) 0%, rgba(21,101,192,0.03) 100%)";

const TAB_OVERVIEW = 0;
const TAB_CARGO = 1;
const TAB_FILES = 2;
const TAB_MAP = 3;
const TAB_NOTES = 4;
const TAB_TERMINAL_INFO = 5;
const TAB_DEBUGGING = 6;

const getEntityId = (entity: any) => {
  return entity?.["@id"] ?? entity?.id ?? null;
};

const normalizeApiId = (value: any) => {
  if (!value) {
    return null;
  }

  if (typeof value === "string") {
    return value;
  }

  return value["@id"] ?? null;
};

const getApiRequestUrl = (requestUrl: string) => {
  const baseUrl = (process.env.REACT_APP_API_URL || "").replace(/\/+$/g, "");
  const isAbsoluteUrl = /^https?:\/\//i.test(requestUrl);
  const baseAlreadyIncludesApi =
    /\/api$/i.test(baseUrl) && /^\/api(?:\/|$)/i.test(requestUrl);
  const normalizedRequestUrl = baseAlreadyIncludesApi
    ? requestUrl.replace(/^\/api(?=\/|$)/i, "")
    : requestUrl;

  return isAbsoluteUrl
    ? requestUrl
    : `${baseUrl}${normalizedRequestUrl.startsWith("/") ? "" : "/"}${
        normalizedRequestUrl
      }`;
};

const patchEntity = async (entity: any, payload: Record<string, any>) => {
  const saveUrl = entity?.["@id"] || entity?.id;

  if (!saveUrl) {
    throw new Error("This item cannot be edited");
  }

  const entityUrl = String(saveUrl);
  const response = await fetch(getApiRequestUrl(entityUrl), {
    method: "PATCH",
    headers: {
      "Content-Type": "application/merge-patch+json",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error("Save failed");
  }

  if (response.status === 204) {
    return { ...entity, ...payload };
  }

  const responseText = await response.text();
  return responseText ? JSON.parse(responseText) : { ...entity, ...payload };
};

const createEntity = async (collection: string, payload: Record<string, any>) => {
  const response = await fetch(getApiRequestUrl(`/api/${collection}`), {
    method: "POST",
    headers: {
      "Content-Type": "application/ld+json",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error("Create failed");
  }

  return response.json();
};

const uploadFileEntity = async (file: File) => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(getApiRequestUrl("/api/files"), {
    method: "POST",
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const responseText = await response.text();
    let errorMessage = responseText || "File upload failed";

    if (responseText) {
      try {
        const responseData = JSON.parse(responseText);
        errorMessage =
          responseData.detail ||
          responseData["hydra:description"] ||
          responseData.violations
            ?.map((violation: any) => violation.message)
            .filter(Boolean)
            .join("\n") ||
          errorMessage;
      } catch {
        // Keep the plain response text when the API did not return JSON.
      }
    }

    throw new Error(errorMessage);
  }

  return response.json();
};

const deleteEntity = async (entity: any) => {
  const entityUrl = getEntityId(entity);

  if (!entityUrl) {
    throw new Error("This item cannot be deleted");
  }

  const response = await fetch(getApiRequestUrl(String(entityUrl)), {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  });

  if (!response.ok) {
    throw new Error("Delete failed");
  }
};

const CONTACT_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isValidContactEmail = (value?: string | null) => {
  const email = value?.trim();
  if (!email) return true;
  return CONTACT_EMAIL_REGEX.test(email);
};

const normalizeContactForForm = (contact: any = {}) => ({
  ...(contact?.["@id"] ? { "@id": contact["@id"] } : {}),
  ...(contact?.id ? { id: contact.id } : {}),
  name: contact?.name ?? "",
  phoneNumber: contact?.phoneNumber ?? "",
  email: contact?.email ?? "",
});

const normalizeContactsForSubmit = (contacts: any[]) =>
  contacts
    .map((contact: any) => ({
      ...(contact?.["@id"] ? { "@id": contact["@id"] } : {}),
      ...(contact?.id ? { id: contact.id } : {}),
      name: contact?.name?.trim() || null,
      phoneNumber: contact?.phoneNumber?.trim() || null,
      email: contact?.email?.trim() || null,
    }))
    .filter(
      (contact: any) =>
        contact["@id"] || contact.id || contact.name || contact.phoneNumber || contact.email
    );

const getDefaultContact = () => ({
  name: "",
  phoneNumber: "",
  email: "",
});

type InlineEditContextValue = {
  activeEditorId: string | null;
  setActiveEditorId: (editorId: string | null) => void;
};

const InlineEditContext = createContext<InlineEditContextValue>({
  activeEditorId: null,
  setActiveEditorId: () => undefined,
});

const getContainerIdentity = (container: any) => {
  const id = normalizeApiId(container);

  if (id) {
    return String(id);
  }

  if (container?.id !== null && container?.id !== undefined) {
    return `/api/containers/${container.id}`;
  }

  return null;
};

const buildContainerLookup = (containers: any[]) => {
  const map = new Map<string, any>();

  (containers || []).forEach((container: any) => {
    const apiId = normalizeApiId(container);

    if (apiId) {
      const normalizedApiId = String(apiId);
      const numericId = normalizedApiId.split("/").pop();

      map.set(normalizedApiId, container);

      if (numericId) {
        map.set(numericId, container);
        map.set(`/api/containers/${numericId}`, container);
      }
    }

    if (container?.id !== null && container?.id !== undefined) {
      map.set(String(container.id), container);
      map.set(`/api/containers/${container.id}`, container);
    }
  });

  return map;
};

const resolveContainerFromCollection = (
  containerRef: any,
  containerById: Map<string, any>
) => {
  const id = normalizeApiId(containerRef);

  if (id) {
    const normalizedId = String(id);
    const numericId = normalizedId.split("/").pop();

    return (
      containerById.get(normalizedId) ||
      (numericId ? containerById.get(numericId) : undefined) ||
      (numericId
        ? containerById.get(`/api/containers/${numericId}`)
        : undefined) ||
      containerRef
    );
  }

  if (containerRef?.id !== null && containerRef?.id !== undefined) {
    return (
      containerById.get(String(containerRef.id)) ||
      containerById.get(`/api/containers/${containerRef.id}`) ||
      containerRef
    );
  }

  return containerRef;
};

const uniqueContainersById = (containers: any[]) => {
  const seen = new Set<string>();
  const uniqueContainers: any[] = [];

  containers.forEach((container: any) => {
    const id = getContainerIdentity(container);

    if (!id) {
      uniqueContainers.push(container);
      return;
    }

    if (seen.has(id)) {
      return;
    }

    seen.add(id);
    uniqueContainers.push(container);
  });

  return uniqueContainers;
};

const sumContainerTeu = (containers: any[]) => {
  return containers.reduce((total, container) => {
    const teu = Number(container?.containerType?.teu ?? 0);

    return total + (Number.isFinite(teu) ? teu : 0);
  }, 0);
};

const sumContainerWeightInTonnes = (containers: any[]) => {
  return containers.reduce((total, container) => {
    const weight = Number(container?.weight ?? 0);

    if (!Number.isFinite(weight) || weight <= 0) {
      return total;
    }

    return total + weight / 1000;
  }, 0);
};

const formatStatsNumber = (value: number, decimals = 0) => {
  if (!Number.isFinite(value)) {
    return decimals === 0 ? "0" : "0.00";
  }

  return new Intl.NumberFormat("nl-BE", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
};

const getStatsFromContainers = (containers: any[]) => {
  return {
    teu: sumContainerTeu(containers),
    containers: containers.length,
    weight: sumContainerWeightInTonnes(containers),
  };
};

const formatDateTime = (value?: any) => {
  if (!value) {
    return "—";
  }

  try {
    return formatDateToString(value, "DD/MM/YYYY HH:mm");
  } catch {
    return "—";
  }
};

const formatDateTimeInput = (value?: any) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60_000).toISOString().slice(0, 16);
};

const hexToRgba = (hex?: string | null, opacity = 0.12) => {
  if (!hex) {
    return `rgba(255, 228, 196, ${opacity})`;
  }

  const normalizedHex = hex.replace("#", "");

  if (![3, 6].includes(normalizedHex.length)) {
    return `rgba(255, 228, 196, ${opacity})`;
  }

  const fullHex =
    normalizedHex.length === 3
      ? normalizedHex
          .split("")
          .map((char) => char + char)
          .join("")
      : normalizedHex;

  const red = parseInt(fullHex.slice(0, 2), 16);
  const green = parseInt(fullHex.slice(2, 4), 16);
  const blue = parseInt(fullHex.slice(4, 6), 16);

  if ([red, green, blue].some((value) => Number.isNaN(value))) {
    return `rgba(255, 228, 196, ${opacity})`;
  }

  return `rgba(${red}, ${green}, ${blue}, ${opacity})`;
};

const normalizeLinkUrl = (url?: string | null) => {
  const trimmedUrl = url?.trim();

  if (!trimmedUrl) {
    return "";
  }

  if (/^https?:\/\//i.test(trimmedUrl)) {
    return trimmedUrl;
  }

  return `https://${trimmedUrl}`;
};

const getLinkName = (link: any, index: number) => {
  return link?.name?.trim() || link?.url?.trim() || `Link ${index + 1}`;
};

const getFileName = (file: any) => {
  return (
    file?.description ??
    file?.discription ??
    file?.name ??
    file?.fileName ??
    file?.filename ??
    "File"
  );
};

const getFileContentUrl = (file: any) => {
  return file?.contentUrl ?? file?.url ?? "";
};

const getFileExtension = (file: any) => {
  const fileUrl = getFileContentUrl(file);
  const fileName = getFileName(file);
  const source = fileUrl || fileName;
  const cleanSource = source.split("?")[0].split("#")[0];
  const extension = cleanSource.split(".").pop();

  return extension ? extension.toLowerCase() : "";
};

const isImageFile = (file: any) => {
  return ["png", "jpg", "jpeg", "gif", "webp", "svg"].includes(
    getFileExtension(file)
  );
};

const isPdfFile = (file: any) => {
  return getFileExtension(file) === "pdf";
};

const getTranslatedLabel = (t: any, key: string, fallback: string) => {
  const translated = t(key);

  return translated && translated !== key ? translated : fallback;
};

const FileTypeIcon = ({ file }: { file: any }) => {
  if (isImageFile(file)) {
    return <ImageIcon fontSize="small" />;
  }

  if (isPdfFile(file)) {
    return <PictureAsPdfIcon fontSize="small" />;
  }

  return <InsertDriveFileSharpIcon fontSize="small" />;
};

const FileDropzoneGallery = ({
  files,
  uploadingFiles,
  uploadError,
  onDrop,
  onPreview,
  compact = false,
  t,
  sx,
}: {
  files: any[];
  uploadingFiles: File[];
  uploadError: string | null;
  onDrop: (files: File[]) => Promise<void>;
  onPreview: (file: any) => void;
  compact?: boolean;
  t: any;
  sx?: any;
}) => {
  const dropzone = useDropzone({
    onDrop,
    noKeyboard: true,
    disabled: uploadingFiles.length > 0,
  });
  const cardHeight = compact ? 104 : 138;

  return (
      <Box
        {...dropzone.getRootProps()}
        sx={{
          border: "2px dashed",
          borderColor: dropzone.isDragActive ? "primary.main" : "divider",
          borderRadius: 2,
          backgroundColor: dropzone.isDragActive
            ? "action.hover"
            : "background.paper",
          p: compact ? 1 : 1.5,
          cursor: uploadingFiles.length > 0 ? "wait" : "pointer",
          transition: "border-color 0.2s ease, background-color 0.2s ease",
          "&:hover": {
            borderColor: "primary.main",
            backgroundColor: "action.hover",
          },
          ...sx,
        }}
      >
      <Box component="input" {...dropzone.getInputProps()} />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: compact
            ? "repeat(auto-fill, minmax(100px, 1fr))"
            : "repeat(auto-fill, minmax(150px, 1fr))",
          gap: 1,
        }}
      >
        {files.map((file: any, index: number) => {
          const fileName = getFileName(file);
          const contentUrl = getFileContentUrl(file);
          const fileKey = getEntityId(file) || `file-${index}`;

          return (
            <Box
              key={fileKey}
              role="button"
              tabIndex={0}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onPreview(file);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  event.stopPropagation();
                  onPreview(file);
                }
              }}
              sx={{
                position: "relative",
                height: cardHeight,
                borderRadius: 1.5,
                border: "1px solid",
                borderColor: "divider",
                overflow: "hidden",
                backgroundColor: "background.default",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "text.secondary",
                cursor: "pointer",
                transition:
                  "border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease",
                "&:hover": {
                  borderColor: "primary.main",
                  boxShadow: 2,
                  transform: "translateY(-1px)",
                },
              }}
            >
              {isImageFile(file) && contentUrl ? (
                <Box
                  component="img"
                  src={contentUrl}
                  alt={fileName}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <FileTypeIcon file={file} />
              )}

              <Box
                sx={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  bottom: 0,
                  px: 0.8,
                  py: 0.65,
                  color: "common.white",
                  background:
                    "linear-gradient(to top, rgba(0,0,0,0.78), rgba(0,0,0,0.08))",
                }}
              >
                <Tooltip title={fileName}>
                  <Typography
                    display="block"
                    variant="caption"
                    noWrap
                    lineHeight={1.2}
                  >
                    {fileName}
                  </Typography>
                </Tooltip>
              </Box>
            </Box>
          );
        })}

        {uploadingFiles.map((file, index) => (
          <Box
            key={`uploading-${file.name}-${index}`}
            sx={{
              position: "relative",
              height: cardHeight,
              borderRadius: 1.5,
              border: "1px solid",
              borderColor: "primary.main",
              backgroundColor: "background.default",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "text.secondary",
              overflow: "hidden",
            }}
          >
            <FileTypeIcon file={file} />
            <CircularProgress
              size={28}
              sx={{ position: "absolute", top: "50%", left: "50%", m: "-14px" }}
            />
            <Box
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                px: 0.8,
                py: 0.65,
                color: "common.white",
                background:
                  "linear-gradient(to top, rgba(0,0,0,0.78), rgba(0,0,0,0.08))",
              }}
            >
              <Typography display="block" variant="caption" noWrap>
                {file.name}
              </Typography>
            </Box>
          </Box>
        ))}

        <Box
          sx={{
            minHeight: cardHeight,
            borderRadius: 1.5,
            border: "1px dashed",
            borderColor: dropzone.isDragActive ? "primary.main" : "divider",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.75,
            px: 1,
            textAlign: "center",
            color: dropzone.isDragActive ? "primary.main" : "text.secondary",
          }}
        >
          <UploadFileSharpIcon />
          <Typography variant="caption">
            {t("dragAndDropFiles")}
          </Typography>
        </Box>
      </Box>

      {uploadError ? (
        <Alert severity="error" sx={{ mt: 1 }}>
          {uploadError}
        </Alert>
      ) : null}
    </Box>
  );
};

const SectionCard = ({
  title,
  children,
  action,
  sx,
  contentSx,
}: {
  title: React.ReactNode;
  children: React.ReactNode;
  action?: React.ReactNode;
  sx?: any;
  contentSx?: any;
}) => {
  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: CARD_RADIUS,
        border: SOFT_BORDER,
        backgroundColor: "#ffffff",
        overflow: "hidden",
        ...sx,
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.35,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
        }}
      >
        <Typography fontWeight={900} fontSize="0.95rem">
          {title}
        </Typography>

        {action}
      </Box>

      <Box sx={{ p: 2, ...contentSx }}>{children}</Box>
    </Paper>
  );
};

const InfoRow = ({
  label,
  value,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
}) => {
  return (
    <Box
      sx={{
        py: 1.1,
        borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
        "&:last-of-type": {
          borderBottom: "none",
        },
      }}
    >
      <Typography
        sx={{
          fontSize: "0.75rem",
          color: "text.secondary",
          mb: 0.3,
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          fontSize: "0.9rem",
          color: "text.primary",
          fontWeight: 600,
          wordBreak: "break-word",
        }}
      >
        {value || "—"}
      </Typography>
    </Box>
  );
};

const TerminalUrlPills = ({
  links,
  terminalColor,
  terminalSoftBackground,
  terminalHoverBackground,
  terminalBorderBackground,
  openLink,
  t,
}: {
  links: any[];
  terminalColor: string;
  terminalSoftBackground: string;
  terminalHoverBackground: string;
  terminalBorderBackground: string;
  openLink: (url?: string | null) => void;
  t: any;
}) => {
  if (!links.length) {
    return <Alert severity="info">{t("noLinksForTerminal")}</Alert>;
  }

  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      {links.map((link: any, index: number) => {
        const linkName = getLinkName(link, index);
        const normalizedUrl = normalizeLinkUrl(link?.url);

        return (
          <Tooltip
            key={`terminal-url-pill-${index}`}
            title={normalizedUrl || linkName}
            arrow
          >
            <Chip
              clickable={Boolean(normalizedUrl)}
              icon={<LinkIcon />}
              label={linkName}
              onClick={() => openLink(link?.url)}
              variant="outlined"
              sx={{
                height: 34,
                maxWidth: "100%",
                borderRadius: "999px",
                px: 0.35,
                borderColor: terminalBorderBackground,
                backgroundColor: terminalSoftBackground,
                color: "text.primary",
                fontWeight: 800,
                boxShadow: "0 2px 6px rgba(15, 23, 42, 0.06)",
                transition:
                  "background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease",
                "& .MuiChip-icon": {
                  ml: 1,
                  color: terminalColor,
                  fontSize: 18,
                },
                "& .MuiChip-label": {
                  px: 1,
                  maxWidth: 220,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                },
                "&:hover": {
                  borderColor: terminalColor,
                  backgroundColor: terminalHoverBackground,
                  boxShadow: "0 4px 12px rgba(15, 23, 42, 0.12)",
                  transform: "translateY(-1px)",
                },
              }}
            />
          </Tooltip>
        );
      })}
    </Stack>
  );
};

const TeuIcon = () => {
  return (
    <Box
      sx={{
        width: 28,
        height: 20,
        border: "2px solid currentColor",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "0.55rem",
        fontWeight: 950,
        lineHeight: 1,
      }}
    >
      TEU
    </Box>
  );
};

const ContainerStackIcon = () => {
  return (
    <Box
      sx={{
        width: 30,
        height: 20,
        border: "2px solid currentColor",
        borderRadius: "2px",
        display: "grid",
        gridTemplateColumns: "repeat(6, 1fr)",
        gap: "1.5px",
        padding: "2px",
        boxSizing: "border-box",
      }}
    >
      {Array.from({ length: 6 }).map((_, index) => (
        <Box
          key={index}
          sx={{
            width: "100%",
            height: "100%",
            backgroundColor: "currentColor",
            borderRadius: "1px",
          }}
        />
      ))}
    </Box>
  );
};

const TimelineStep = ({
  label,
  value,
  color,
  icon,
  field,
  entity,
  onSave,
}: {
  label: string;
  value: string;
  color: string;
  icon: React.ReactNode;
  field: string;
  entity: any;
  onSave: (savedEntity?: any) => Promise<void> | void;
}) => {
  return (
    <Box
      sx={{
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
      }}
    >
      <Typography
        noWrap
        sx={{
          mb: 1.4,
          maxWidth: "100%",
          color,
          fontSize: "0.82rem",
          fontWeight: 850,
          lineHeight: 1.2,
        }}
      >
        {label}
      </Typography>

      <Box
        sx={{
          width: 58,
          height: 58,
          borderRadius: "50%",
          border: `2px solid ${color}`,
          backgroundColor: "#ffffff",
          color,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          zIndex: 2,
          boxShadow: "0 2px 7px rgba(15, 23, 42, 0.08)",
          "& svg": {
            fontSize: 28,
          },
        }}
      >
        {icon}

        <Box
          sx={{
            position: "absolute",
            left: "50%",
            bottom: -21,
            width: 10,
            height: 10,
            borderRadius: "50%",
            backgroundColor: color,
            transform: "translateX(-50%)",
          }}
        />

        <Box
          sx={{
            position: "absolute",
            left: "50%",
            bottom: -15,
            height: 15,
            width: 2,
            backgroundColor: color,
            transform: "translateX(-50%)",
          }}
        />
      </Box>

      <Paper
        variant="outlined"
        sx={{
          mt: 3.6,
          width: "100%",
          maxWidth: 230,
          py: 1,
          px: 1.25,
          borderRadius: 1,
          minHeight: 42,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderColor: "rgba(15, 23, 42, 0.14)",
          backgroundColor: "#ffffff",
        }}
      >
        <InlineEditableField
          label={label}
          hideLabel
          dense
          value={entity?.[field] || ""}
          displayValue={value}
          field={field}
          entity={entity}
          onSave={onSave}
          type="datetime-local"
        />
      </Paper>
    </Box>
  );
};

const TerminalCallTimeline = ({
  terminalCall,
  t,
  onSave,
}: {
  terminalCall: any;
  t: any;
  onSave: (savedEntity?: any) => Promise<void> | void;
}) => {
  const blue = ONBOARD_COLOR;
  const green = UNLOADING_COLOR;

  return (
    <SectionCard
      title={getTranslatedLabel(t, "timeline", "Timeline")}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" mb={3} minWidth={0}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 1.5,
            backgroundColor: "rgba(25, 118, 210, 0.1)",
            color: "primary.main",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <CalendarMonthIcon />
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: "0.78rem",
              color: "text.secondary",
              fontWeight: 700,
            }}
          >
            {getTranslatedLabel(
              t,
              "plannedTimeReference",
              "Planned time reference"
            )}
          </Typography>

          <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
            <InlineEditableField
              label={getTranslatedLabel(t, "planned", "Planned")}
              hideLabel
              dense
              value={terminalCall?.planned || ""}
              displayValue={formatDateTime(terminalCall?.planned)}
              field="planned"
              entity={terminalCall}
              onSave={onSave}
              type="datetime-local"
            />

            <Tooltip title={getTranslatedLabel(t, "planned", "Planned")}>
              <InfoIcon color="primary" fontSize="small" />
            </Tooltip>
          </Stack>
        </Box>
      </Stack>

      <Box
        sx={{
          position: "relative",
          px: {
            xs: 0,
            md: 3,
          },
          pb: 0.5,
          overflowX: "auto",
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(15, 23, 42, 0.28) transparent",
          "&::-webkit-scrollbar": {
            width: 6,
            height: 6,
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(15, 23, 42, 0.25)",
            borderRadius: 999,
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "rgba(15, 23, 42, 0.38)",
          },
        }}
      >
        <Box
          sx={{
            minWidth: 760,
            position: "relative",
            pt: 0.5,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              left: "12.5%",
              right: "12.5%",
              top: 58,
              height: 4,
              borderRadius: 999,
              background: `linear-gradient(90deg, ${blue} 0%, ${blue} 33.333%, ${green} 33.333%, ${green} 66.666%, ${blue} 66.666%, ${blue} 100%)`,
              zIndex: 1,
            }}
          />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
              columnGap: 3,
              alignItems: "start",
              position: "relative",
              zIndex: 2,
            }}
          >
            <TimelineStep
              label={getTranslatedLabel(t, "arrival", "Arrival")}
              value={formatDateTime(terminalCall?.arrival || terminalCall?.planned)}
              color={blue}
              icon={<DirectionsBoatIcon />}
              field="arrival"
              entity={terminalCall}
              onSave={onSave}
            />

            <TimelineStep
              label={getTranslatedLabel(t, "operationStart", "Operation start")}
              value={formatDateTime(terminalCall?.operationStart)}
              color={green}
              icon={<PlayArrowIcon />}
              field="operationStart"
              entity={terminalCall}
              onSave={onSave}
            />

            <TimelineStep
              label={getTranslatedLabel(t, "operationEnd", "Operation end")}
              value={formatDateTime(terminalCall?.operationEnd)}
              color={green}
              icon={<StopIcon />}
              field="operationEnd"
              entity={terminalCall}
              onSave={onSave}
            />

            <TimelineStep
              label={getTranslatedLabel(t, "departure", "Departure")}
              value={formatDateTime(terminalCall?.departure)}
              color={blue}
              icon={<DirectionsBoatIcon />}
              field="departure"
              entity={terminalCall}
              onSave={onSave}
            />
          </Box>
        </Box>

        <Stack direction="row" spacing={3} alignItems="center" mt={2.5}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: 28,
                height: 3,
                borderRadius: 999,
                backgroundColor: blue,
              }}
            />

            <Typography fontSize="0.78rem">
              {getTranslatedLabel(t, "arrival", "Arrival")} /{" "}
              {getTranslatedLabel(t, "departure", "Departure")}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: 28,
                height: 3,
                borderRadius: 999,
                backgroundColor: green,
              }}
            />

            <Typography fontSize="0.78rem">
              {getTranslatedLabel(t, "operation", "Operation")}
            </Typography>
          </Stack>
        </Stack>
      </Box>
    </SectionCard>
  );
};

const TerminalCallOverviewStats = ({
  terminalCall,
  allContainers,
  t,
}: OverviewStatsProps) => {
  const containerById = useMemo(() => {
    return buildContainerLookup(allContainers || []);
  }, [allContainers]);

  const unloadingContainers = useMemo(() => {
    return (terminalCall?.unloadingContainers || []).map((container: any) =>
      resolveContainerFromCollection(container, containerById)
    );
  }, [terminalCall, containerById]);

  const unloadingContainerIds = useMemo(() => {
    return new Set(
      unloadingContainers
        .map((container: any) => getContainerIdentity(container))
        .filter(Boolean)
    );
  }, [unloadingContainers]);

  const loadingContainers = useMemo(() => {
    return (terminalCall?.loadingContainers || []).map((container: any) =>
      resolveContainerFromCollection(container, containerById)
    );
  }, [terminalCall, containerById]);

  const arrivalContainers = useMemo(() => {
    const arrivalIds = getTerminalCallArrivalStateContainerIds(terminalCall);

    return arrivalIds
      .map((id: string) => {
        const normalizedId = String(id);
        const numericId = normalizedId.split("/").pop();

        return (
          containerById.get(normalizedId) ||
          (numericId ? containerById.get(numericId) : undefined) ||
          (numericId
            ? containerById.get(`/api/containers/${numericId}`)
            : undefined)
        );
      })
      .filter(Boolean);
  }, [terminalCall, containerById]);

  const onboardContainers = useMemo(() => {
    return uniqueContainersById([
      ...loadingContainers,
      ...arrivalContainers,
    ]).filter((container: any) => {
      const id = getContainerIdentity(container);

      if (!id) {
        return true;
      }

      return !unloadingContainerIds.has(id);
    });
  }, [loadingContainers, arrivalContainers, unloadingContainerIds]);

  const unloadingStats = getStatsFromContainers(unloadingContainers);
  const loadingStats = getStatsFromContainers(loadingContainers);
  const onboardStats = getStatsFromContainers(onboardContainers);

  const columns = [
    {
      key: "unloading",
      label: getTranslatedLabel(t, "unloading", "Unloading"),
      subtitle: getTranslatedLabel(t, "release", "Release"),
      color: UNLOADING_COLOR,
      background: UNLOADING_BACKGROUND,
      bodyBackground: "rgba(22,163,74,0.02)",
      icon: <LoadingIcon sx={{ fontSize: 22 }} />,
      stats: unloadingStats,
      tooltip: `${getTranslatedLabel(t, "unloading", "Unloading")} (${getTranslatedLabel(
        t,
        "release",
        "Release"
      )})`,
    },
    {
      key: "loading",
      label: getTranslatedLabel(t, "loading", "Loading"),
      subtitle: getTranslatedLabel(t, "add", "Add"),
      color: LOADING_COLOR,
      background: LOADING_BACKGROUND,
      bodyBackground: "rgba(234,179,8,0.035)",
      icon: <UnloadingIcon sx={{ fontSize: 22 }} />,
      stats: loadingStats,
      tooltip: `${getTranslatedLabel(t, "loading", "Loading")} (${getTranslatedLabel(
        t,
        "add",
        "Add"
      )})`,
    },
    {
      key: "onboard",
      label: getTranslatedLabel(t, "afterVisit", "After visit"),
      subtitle: getTranslatedLabel(t, "onBoard", "On board"),
      color: ONBOARD_COLOR,
      background: ONBOARD_BACKGROUND,
      bodyBackground: "rgba(21,101,192,0.02)",
      icon: <DirectionsBoatIcon sx={{ fontSize: 25 }} />,
      stats: onboardStats,
      tooltip: `${getTranslatedLabel(
        t,
        "afterVisit",
        "After visit"
      )} (${getTranslatedLabel(t, "onBoard", "On board")})`,
    },
  ];

  const rows = [
    {
      key: "teu",
      label: getTranslatedLabel(t, "teu", "TEU"),
      icon: <TeuIcon />,
      getValue: (stats: any) => formatStatsNumber(stats.teu, 0),
    },
    {
      key: "containers",
      label: getTranslatedLabel(t, "containers", "Containers"),
      icon: <ContainerStackIcon />,
      getValue: (stats: any) => formatStatsNumber(stats.containers, 0),
    },
    {
      key: "weight",
      label: getTranslatedLabel(t, "weight", "Weight"),
      icon: <ScaleIcon sx={{ fontSize: 23 }} />,
      getValue: (stats: any) => `${formatStatsNumber(stats.weight, 2)} t`,
    },
  ];

  return (
    <SectionCard title={getTranslatedLabel(t, "overview", "Overview")}>
      <Box
        sx={{
          width: "100%",
          borderRadius: WRAPPER_BORDER_RADIUS,
          overflow: "hidden",
          border: "1px solid rgba(15, 23, 42, 0.1)",
          backgroundColor: "#ffffff",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "minmax(72px, 0.8fr) repeat(3, minmax(0, 1fr))",
            borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
          }}
        >
          <Box
            sx={{
              minHeight: 56,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: 0.5,
              borderRight: "1px solid rgba(15, 23, 42, 0.08)",
            }}
          >
            <Tooltip title={getTranslatedLabel(t, "overview", "Overview")} arrow>
              <Typography
                noWrap
                sx={{
                  maxWidth: "100%",
                  fontWeight: 950,
                  fontSize: "0.58rem",
                  textTransform: "uppercase",
                  color: "text.primary",
                }}
              >
                {getTranslatedLabel(t, "overview", "Overview")}
              </Typography>
            </Tooltip>
          </Box>

          {columns.map((column) => (
            <Box
              key={column.key}
              sx={{
                minWidth: 0,
                minHeight: 56,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.1,
                px: 0.4,
                textAlign: "center",
                background: column.background,
                borderRight:
                  column.key === "onboard"
                    ? "none"
                    : "1px solid rgba(15, 23, 42, 0.08)",
                color: column.color,
              }}
            >
              {column.icon}

              <Tooltip title={column.tooltip} arrow>
                <Box sx={{ minWidth: 0, maxWidth: "100%" }}>
                  <Typography
                    noWrap
                    sx={{
                      mt: 0.1,
                      maxWidth: "100%",
                      fontSize: "0.52rem",
                      fontWeight: 950,
                      textTransform: "uppercase",
                      lineHeight: 1,
                    }}
                  >
                    {column.label}
                  </Typography>

                  <Typography
                    noWrap
                    sx={{
                      maxWidth: "100%",
                      fontSize: "0.45rem",
                      fontWeight: 850,
                      textTransform: "uppercase",
                      lineHeight: 1,
                      opacity: 0.85,
                    }}
                  >
                    ({column.subtitle})
                  </Typography>
                </Box>
              </Tooltip>
            </Box>
          ))}
        </Box>

        {rows.map((row, rowIndex) => (
          <Box
            key={row.key}
            sx={{
              display: "grid",
              gridTemplateColumns:
                "minmax(72px, 0.8fr) repeat(3, minmax(0, 1fr))",
              minHeight: 46,
              borderBottom:
                rowIndex === rows.length - 1
                  ? "none"
                  : "1px solid rgba(15, 23, 42, 0.08)",
            }}
          >
            <Box
              sx={{
                minWidth: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 0.45,
                borderRight: "1px solid rgba(15, 23, 42, 0.08)",
                color: "text.secondary",
                "& svg": {
                  fontSize: "21px",
                },
                "& > div": {
                  transform: "scale(0.76)",
                },
              }}
            >
              <Tooltip title={row.label} arrow>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    lineHeight: 1,
                  }}
                >
                  {row.icon}
                </Box>
              </Tooltip>
            </Box>

            {columns.map((column) => (
              <Box
                key={`${row.key}-${column.key}`}
                sx={{
                  minWidth: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  px: 0.35,
                  textAlign: "center",
                  backgroundColor: column.bodyBackground,
                  borderRight:
                    column.key === "onboard"
                      ? "none"
                      : "1px solid rgba(15, 23, 42, 0.08)",
                }}
              >
                <Typography
                  noWrap
                  sx={{
                    maxWidth: "100%",
                    fontSize: row.key === "weight" ? "0.84rem" : "1.02rem",
                    fontWeight: 950,
                    lineHeight: 1,
                    color: column.color,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {row.getValue(column.stats)}
                </Typography>
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </SectionCard>
  );
};

const getContainerDisplayWeight = (container: any) => {
  const weight = Number(container?.weight ?? 0);

  if (!Number.isFinite(weight) || weight <= 0) {
    return "—";
  }

  return `${formatStatsNumber(weight / 1000, 3)} t`;
};

const getCargoCompletionDate = (value?: any) => {
  if (!value) {
    return "—";
  }

  return formatDateTime(value);
};

const CargoContainerRow = ({
  container,
  completed,
  completedAt,
  completedLabel,
  pendingLabel,
  completedTooltip,
  color,
  t,
}: {
  container: any;
  completed: boolean;
  completedAt?: any;
  completedLabel: string;
  pendingLabel: string;
  completedTooltip: string;
  color: string;
  t: any;
}) => {
  const uid = container?.uid || getContainerIdentity(container) || "—";
  const type = container?.containerType?.name || "—";
  const teu = container?.containerType?.teu ?? "—";
  const weight = getContainerDisplayWeight(container);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.25,
        borderRadius: 1.75,
        borderColor: completed
          ? hexToRgba(UNLOADING_COLOR, 0.28)
          : hexToRgba(color, 0.28),
        backgroundColor: completed
          ? hexToRgba(UNLOADING_COLOR, 0.06)
          : hexToRgba(color, 0.06),
        transition:
          "border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease",
        "&:hover": {
          borderColor: completed ? UNLOADING_COLOR : color,
          boxShadow: "0 8px 22px rgba(15, 23, 42, 0.09)",
          transform: "translateY(-1px)",
        },
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1.5,
            flexShrink: 0,
            backgroundColor: completed
              ? hexToRgba(UNLOADING_COLOR, 0.13)
              : hexToRgba(color, 0.13),
            color: completed ? UNLOADING_COLOR : color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {completed ? (
            <CheckCircleIcon sx={{ fontSize: 24 }} />
          ) : (
            <ContainerStackIcon />
          )}
        </Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
            <Typography
              noWrap
              sx={{
                minWidth: 0,
                fontSize: "0.92rem",
                fontWeight: 950,
                color: "text.primary",
              }}
            >
              {uid}
            </Typography>

            {container?.importFile ? (
              <Tooltip title={container.importFile}>
                <FileOpenIcon
                  sx={{
                    color: "#008000",
                    fontSize: 17,
                    flexShrink: 0,
                  }}
                />
              </Tooltip>
            ) : null}

            {container?.empty ? (
              <Chip
                size="small"
                label={getTranslatedLabel(t, "empty", "MT")}
                sx={{
                  height: 20,
                  fontSize: "0.66rem",
                  fontWeight: 900,
                }}
              />
            ) : null}
          </Stack>

          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ mt: 0.65 }}
          >
            <Chip
              size="small"
              label={type}
              variant="outlined"
              sx={{
                height: 22,
                fontSize: "0.67rem",
                fontWeight: 800,
                borderColor: "rgba(15, 23, 42, 0.12)",
              }}
            />

            <Chip
              size="small"
              label={`${teu} ${getTranslatedLabel(t, "teu", "TEU")}`}
              variant="outlined"
              sx={{
                height: 22,
                fontSize: "0.67rem",
                fontWeight: 800,
                borderColor: "rgba(15, 23, 42, 0.12)",
              }}
            />

            <Chip
              size="small"
              label={weight}
              icon={<ScaleIcon sx={{ fontSize: "14px !important" }} />}
              variant="outlined"
              sx={{
                height: 22,
                fontSize: "0.67rem",
                fontWeight: 800,
                borderColor: "rgba(15, 23, 42, 0.12)",
                "& .MuiChip-icon": {
                  ml: 0.75,
                },
              }}
            />
          </Stack>
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            minWidth: 128,
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Tooltip title={completed ? completedTooltip : pendingLabel} arrow>
            <Chip
              size="small"
              icon={
                completed ? (
                  <CheckCircleIcon sx={{ fontSize: "16px !important" }} />
                ) : undefined
              }
              label={completed ? completedLabel : pendingLabel}
              sx={{
                height: 28,
                fontSize: "0.72rem",
                fontWeight: 900,
                backgroundColor: completed
                  ? hexToRgba(UNLOADING_COLOR, 0.14)
                  : hexToRgba(color, 0.14),
                color: completed ? UNLOADING_COLOR : color,
                "& .MuiChip-icon": {
                  color: completed ? UNLOADING_COLOR : color,
                },
              }}
            />
          </Tooltip>
        </Box>
      </Stack>

      {completed ? (
        <Box
          sx={{
            mt: 1,
            pt: 1,
            borderTop: "1px solid rgba(15, 23, 42, 0.08)",
          }}
        >
          <Typography
            sx={{
              fontSize: "0.72rem",
              color: "text.secondary",
              fontWeight: 700,
            }}
          >
            {getTranslatedLabel(t, "completedAt", "Completed at")}:{" "}
            <Box component="span" sx={{ color: "text.primary", fontWeight: 850 }}>
              {getCargoCompletionDate(completedAt)}
            </Box>
          </Typography>
        </Box>
      ) : null}
    </Paper>
  );
};

const CargoColumn = ({
  title,
  subtitle,
  containers,
  color,
  emptyText,
  warningText,
  successText,
  actionLabel,
  actionDisabled,
  onAction,
  getCompleted,
  getCompletedAt,
  completedLabel,
  pendingLabel,
  completedTooltip,
  t,
}: {
  title: string;
  subtitle: string;
  containers: any[];
  color: string;
  emptyText: string;
  warningText: string;
  successText: string;
  actionLabel: string;
  actionDisabled: boolean;
  onAction: () => Promise<void>;
  getCompleted: (container: any) => boolean;
  getCompletedAt: (container: any) => any;
  completedLabel: string;
  pendingLabel: string;
  completedTooltip: string;
  t: any;
}) => {
  const completedCount = containers.filter(getCompleted).length;
  const allCompleted =
    containers.length > 0 && completedCount === containers.length;
  const hasPending = containers.length > 0 && completedCount < containers.length;

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: CARD_RADIUS,
        border: `1px solid ${hexToRgba(color, 0.22)}`,
        backgroundColor: "#ffffff",
        overflow: "hidden",
        height: "100%",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          background: `linear-gradient(180deg, ${hexToRgba(
            color,
            0.12
          )} 0%, ${hexToRgba(color, 0.035)} 100%)`,
          borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
        }}
      >
        <Stack direction="row" justifyContent="space-between" spacing={1.5}>
          <Stack direction="row" spacing={1.25} alignItems="center" minWidth={0}>
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: 1.5,
                backgroundColor: hexToRgba(color, 0.14),
                color,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <LocalShippingIcon />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                noWrap
                sx={{
                  fontSize: "1rem",
                  fontWeight: 950,
                  color: "text.primary",
                }}
              >
                {title}
              </Typography>

              <Typography
                noWrap
                sx={{
                  fontSize: "0.74rem",
                  color: "text.secondary",
                  fontWeight: 700,
                }}
              >
                {subtitle}
              </Typography>
            </Box>
          </Stack>

          <Chip
            label={`${completedCount}/${containers.length}`}
            sx={{
              height: 30,
              minWidth: 58,
              fontWeight: 950,
              backgroundColor: hexToRgba(color, 0.14),
              color,
            }}
          />
        </Stack>

        {containers.length > 0 ? (
          <Box sx={{ mt: 1.5 }}>
            <Box
              sx={{
                height: 7,
                borderRadius: 999,
                overflow: "hidden",
                backgroundColor: "rgba(15, 23, 42, 0.08)",
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  width: `${
                    containers.length
                      ? (completedCount / containers.length) * 100
                      : 0
                  }%`,
                  borderRadius: 999,
                  backgroundColor: allCompleted ? UNLOADING_COLOR : color,
                  transition: "width 0.2s ease",
                }}
              />
            </Box>
          </Box>
        ) : null}
      </Box>

      <Box sx={{ p: 2 }}>
        {containers.length === 0 ? (
          <Alert severity="info">{emptyText}</Alert>
        ) : (
          <>
            {hasPending && containers.length > 1 ? (
              <Alert severity="warning" sx={{ mb: 1.25 }}>
                {warningText}
              </Alert>
            ) : null}

            {allCompleted ? (
              <Alert severity="success" sx={{ mb: 1.25 }}>
                {successText}
              </Alert>
            ) : null}

            <Stack
              spacing={1}
              sx={{
                maxHeight: "44vh",
                overflowY: "auto",
                pr: 0.5,
                scrollbarWidth: "thin",
                scrollbarColor: "rgba(15, 23, 42, 0.28) transparent",
                "&::-webkit-scrollbar": {
                  width: 6,
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(15, 23, 42, 0.25)",
                  borderRadius: 999,
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  backgroundColor: "rgba(15, 23, 42, 0.38)",
                },
              }}
            >
              {containers.map((container: any, index: number) => (
                <CargoContainerRow
                  key={
                    container?.["@id"] ||
                    container?.id ||
                    `${title}-cargo-container-${index}`
                  }
                  container={container}
                  completed={getCompleted(container)}
                  completedAt={getCompletedAt(container)}
                  completedLabel={completedLabel}
                  pendingLabel={pendingLabel}
                  completedTooltip={completedTooltip}
                  color={color}
                  t={t}
                />
              ))}
            </Stack>

            {!allCompleted ? (
              <Button
                fullWidth
                variant="contained"
                onClick={onAction}
                disabled={actionDisabled}
                sx={{
                  mt: 1.5,
                  textTransform: "none",
                  fontWeight: 900,
                  backgroundColor: color,
                  "&:hover": {
                    backgroundColor: color,
                    filter: "brightness(0.94)",
                  },
                }}
              >
                {actionLabel}
              </Button>
            ) : null}
          </>
        )}
      </Box>
    </Paper>
  );
};

const TerminalCallCargoTab = ({
  loadingContainers,
  unloadingContainers,
  refreshCallback,
  t,
}: {
  loadingContainers: any[];
  unloadingContainers: any[];
  refreshCallback: (data?: any) => Promise<void>;
  t: any;
}) => {
  const dispatch = useDispatch();
  const [isUpdatingLoaded, setIsUpdatingLoaded] = useState(false);
  const [isUpdatingUnloaded, setIsUpdatingUnloaded] = useState(false);

  const loadingStats = getStatsFromContainers(loadingContainers);
  const unloadingStats = getStatsFromContainers(unloadingContainers);
  const totalCargo = loadingContainers.length + unloadingContainers.length;
  const totalTeu = loadingStats.teu + unloadingStats.teu;
  const totalWeight = loadingStats.weight + unloadingStats.weight;

  const handleMarkAllLoaded = async () => {
    setIsUpdatingLoaded(true);

    try {
      const current = new Date();

      await Promise.all(
        loadingContainers.map((container: any) => {
          if (container.loadedDate) {
            return Promise.resolve();
          }

          return dispatch(
            (updateSelectedContainer as any)(
              {
                ...container,
                loadedDate: current.toISOString(),
              },
              false
            )
          );
        })
      );

      await refreshCallback();
    } finally {
      setIsUpdatingLoaded(false);
    }
  };

  const handleMarkAllUnloaded = async () => {
    setIsUpdatingUnloaded(true);

    try {
      const current = new Date();

      await Promise.all(
        unloadingContainers.map((container: any) => {
          if (container.unloadedDate) {
            return Promise.resolve();
          }

          return dispatch(
            (updateSelectedContainer as any)(
              {
                ...container,
                unloadedDate: current.toISOString(),
              },
              false
            )
          );
        })
      );

      await refreshCallback();
    } finally {
      setIsUpdatingUnloaded(false);
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            borderRadius: CARD_RADIUS,
            border: SOFT_BORDER,
            backgroundColor: "rgba(15, 23, 42, 0.015)",
          }}
        >
          <Grid container spacing={1.25}>
            <Grid item xs={12} sm={4}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.25,
                  borderRadius: 1.5,
                  backgroundColor: "#ffffff",
                  borderColor: "rgba(15, 23, 42, 0.08)",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: 900,
                    color: "text.secondary",
                    textTransform: "uppercase",
                  }}
                >
                  {getTranslatedLabel(t, "containers", "Containers")}
                </Typography>

                <Typography
                  sx={{
                    mt: 0.35,
                    fontSize: "1.35rem",
                    fontWeight: 950,
                    color: "text.primary",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {formatStatsNumber(totalCargo, 0)}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.25,
                  borderRadius: 1.5,
                  backgroundColor: "#ffffff",
                  borderColor: "rgba(15, 23, 42, 0.08)",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: 900,
                    color: "text.secondary",
                    textTransform: "uppercase",
                  }}
                >
                  {getTranslatedLabel(t, "teu", "TEU")}
                </Typography>

                <Typography
                  sx={{
                    mt: 0.35,
                    fontSize: "1.35rem",
                    fontWeight: 950,
                    color: "text.primary",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {formatStatsNumber(totalTeu, 0)}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.25,
                  borderRadius: 1.5,
                  backgroundColor: "#ffffff",
                  borderColor: "rgba(15, 23, 42, 0.08)",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: 900,
                    color: "text.secondary",
                    textTransform: "uppercase",
                  }}
                >
                  {getTranslatedLabel(t, "weight", "Weight")}
                </Typography>

                <Typography
                  sx={{
                    mt: 0.35,
                    fontSize: "1.35rem",
                    fontWeight: 950,
                    color: "text.primary",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {formatStatsNumber(totalWeight, 2)} t
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      <Grid item xs={12} md={6}>
        <CargoColumn
          title={getTranslatedLabel(t, "loading", "Loading")}
          subtitle={getTranslatedLabel(t, "cargoToLoad", "Cargo to load")}
          containers={loadingContainers}
          color={LOADING_COLOR}
          emptyText={getTranslatedLabel(
            t,
            "noContainersToBeLoadedInThisTerminalCall",
            "No containers to be loaded in this terminal call."
          )}
          warningText={getTranslatedLabel(
            t,
            "notAllContainersMarkedAsLoaded",
            "Not all containers were marked as loaded."
          )}
          successText={getTranslatedLabel(
            t,
            "allContainersHaveBeenLoaded",
            "All containers have been loaded."
          )}
          actionLabel={getTranslatedLabel(t, "markAsLoaded", "Mark as loaded")}
          actionDisabled={isUpdatingLoaded}
          onAction={handleMarkAllLoaded}
          getCompleted={(container: any) => Boolean(container.loadedDate)}
          getCompletedAt={(container: any) => container.loadedDate}
          completedLabel={getTranslatedLabel(t, "loaded", "Loaded")}
          pendingLabel={getTranslatedLabel(t, "pending", "Pending")}
          completedTooltip={getTranslatedLabel(
            t,
            "containerLoaded",
            "Container loaded"
          )}
          t={t}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <CargoColumn
          title={getTranslatedLabel(t, "unloading", "Unloading")}
          subtitle={getTranslatedLabel(t, "cargoToUnload", "Cargo to unload")}
          containers={unloadingContainers}
          color={UNLOADING_COLOR}
          emptyText={getTranslatedLabel(
            t,
            "noContainersToBeUnloadedInThisTerminalCall",
            "No containers to be unloaded in this terminal call."
          )}
          warningText={getTranslatedLabel(
            t,
            "notAllContainersMarkedAsUnloaded",
            "Not all containers were marked as unloaded."
          )}
          successText={getTranslatedLabel(
            t,
            "allContainersHaveBeenUnloaded",
            "All containers have been unloaded."
          )}
          actionLabel={getTranslatedLabel(t, "markAsUnloaded", "Mark as unloaded")}
          actionDisabled={isUpdatingUnloaded}
          onAction={handleMarkAllUnloaded}
          getCompleted={(container: any) => Boolean(container.unloadedDate)}
          getCompletedAt={(container: any) => container.unloadedDate}
          completedLabel={getTranslatedLabel(t, "unloaded", "Unloaded")}
          pendingLabel={getTranslatedLabel(t, "pending", "Pending")}
          completedTooltip={getTranslatedLabel(
            t,
            "containerUnloaded",
            "Container unloaded"
          )}
          t={t}
        />
      </Grid>
    </Grid>
  );
};

const StateValueBlock = ({ value }: { value: any }) => {
  if (value === null || value === undefined || value === "") {
    return (
      <Typography fontSize="0.8rem" color="text.secondary">
        —
      </Typography>
    );
  }

  if (typeof value === "boolean") {
    return (
      <Chip
        size="small"
        label={value ? "true" : "false"}
        color={value ? "success" : "default"}
        sx={{ fontWeight: 800 }}
      />
    );
  }

  if (typeof value === "number") {
    return (
      <Typography fontSize="0.86rem" fontWeight={750}>
        {Number.isInteger(value)
          ? formatStatsNumber(value, 0)
          : formatStatsNumber(value, 2)}
      </Typography>
    );
  }

  if (typeof value === "object") {
    return (
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 1.25,
          maxHeight: 260,
          overflow: "auto",
          borderRadius: 1.25,
          backgroundColor: "rgba(15, 23, 42, 0.04)",
          border: "1px solid rgba(15, 23, 42, 0.08)",
          fontSize: "0.72rem",
          lineHeight: 1.45,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          fontFamily:
            'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(15, 23, 42, 0.28) transparent",
          "&::-webkit-scrollbar": {
            width: 6,
            height: 6,
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(15, 23, 42, 0.25)",
            borderRadius: 999,
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "rgba(15, 23, 42, 0.38)",
          },
        }}
      >
        {JSON.stringify(value, null, 2)}
      </Box>
    );
  }

  return (
    <Typography
      sx={{
        fontSize: "0.86rem",
        fontWeight: 650,
        color: "text.primary",
        wordBreak: "break-word",
      }}
    >
      {String(value)}
    </Typography>
  );
};

const StateSummaryGrid = ({ state, t }: { state: any; t: any }) => {
  if (!state || typeof state !== "object") {
    return (
      <Alert severity="info" sx={{ mt: 1 }}>
        {getTranslatedLabel(
          t,
          "noStateDataAvailable",
          "No state data available."
        )}
      </Alert>
    );
  }

  const entries = Object.entries(state).filter(([_, value]) => {
    return value !== null && value !== undefined && value !== "";
  });

  if (!entries.length) {
    return (
      <Alert severity="info" sx={{ mt: 1 }}>
        {getTranslatedLabel(
          t,
          "noStateDataAvailable",
          "No state data available."
        )}
      </Alert>
    );
  }

  return (
    <Grid container spacing={1} sx={{ mt: 0.5 }}>
      {entries.map(([key, value]) => {
        const isComplex = typeof value === "object" && value !== null;

        return (
          <Grid item xs={12} sm={isComplex ? 12 : 6} key={key}>
            <Paper
              variant="outlined"
              sx={{
                p: 1.15,
                borderRadius: 1.5,
                borderColor: "rgba(15, 23, 42, 0.1)",
                backgroundColor: "#ffffff",
              }}
            >
              <Typography
                sx={{
                  mb: 0.45,
                  fontSize: "0.68rem",
                  fontWeight: 900,
                  color: "text.secondary",
                  textTransform: "uppercase",
                  letterSpacing: "0.02em",
                }}
              >
                {key}
              </Typography>

              <StateValueBlock value={value} />
            </Paper>
          </Grid>
        );
      })}
    </Grid>
  );
};

const DebugStateAccordion = ({
  title,
  state,
  color,
  t,
  defaultExpanded = false,
}: {
  title: string;
  state: any;
  color: string;
  t: any;
  defaultExpanded?: boolean;
}) => {
  const hasState =
    state &&
    typeof state === "object" &&
    Object.keys(state).some(
      (key) =>
        state[key] !== null && state[key] !== undefined && state[key] !== ""
    );

  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      sx={{
        border: "1px solid rgba(15, 23, 42, 0.1)",
        borderRadius: "10px !important",
        overflow: "hidden",
        boxShadow: "none",
        "&:before": {
          display: "none",
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          minHeight: 44,
          backgroundColor: hexToRgba(color, 0.08),
          borderLeft: `4px solid ${color}`,
          "& .MuiAccordionSummary-content": {
            my: 1,
            alignItems: "center",
            gap: 1,
          },
        }}
      >
        <Box
          sx={{
            width: 9,
            height: 9,
            borderRadius: "50%",
            backgroundColor: hasState ? color : "rgba(15, 23, 42, 0.25)",
            flexShrink: 0,
          }}
        />

        <Typography
          sx={{
            fontSize: "0.86rem",
            fontWeight: 900,
            color: "text.primary",
          }}
        >
          {title}
        </Typography>

        <Chip
          size="small"
          label={
            hasState
              ? getTranslatedLabel(t, "available", "Available")
              : getTranslatedLabel(t, "emptyState", "Empty")
          }
          sx={{
            ml: "auto",
            height: 22,
            fontSize: "0.68rem",
            fontWeight: 800,
            backgroundColor: hasState ? hexToRgba(color, 0.14) : "grey.100",
            color: hasState ? color : "text.secondary",
          }}
        />
      </AccordionSummary>

      <AccordionDetails sx={{ p: 1.25, backgroundColor: "#ffffff" }}>
        <StateSummaryGrid state={state} t={t} />
      </AccordionDetails>
    </Accordion>
  );
};

const TerminalCallDebugStates = ({
  terminalCall,
  t,
}: {
  terminalCall: any;
  t: any;
}) => {
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <SectionCard title={getTranslatedLabel(t, "debugging", "Debugging")}>
          <Stack spacing={1}>
            <DebugStateAccordion
              title={getTranslatedLabel(t, "arrivalState", "Arrival State")}
              state={terminalCall?.arrivalState}
              color={UNLOADING_COLOR}
              t={t}
            />

            <DebugStateAccordion
              title={getTranslatedLabel(t, "departureState", "Departure State")}
              state={terminalCall?.departureState}
              color={ONBOARD_COLOR}
              t={t}
            />

            <DebugStateAccordion
              title={getTranslatedLabel(
                t,
                "bargeArrivalState",
                "Barge Arrival State"
              )}
              state={
                terminalCall?.bargeArrivalState ?? terminalCall?.bargearrivalState
              }
              color={UNLOADING_COLOR}
              t={t}
            />

            <DebugStateAccordion
              title={getTranslatedLabel(
                t,
                "bargeDepartureState",
                "Barge Departure State"
              )}
              state={
                terminalCall?.bargeDepartureState ??
                terminalCall?.bargedepartureState
              }
              color={ONBOARD_COLOR}
              t={t}
            />

          </Stack>
        </SectionCard>
      </Grid>
    </Grid>
  );
};

const TerminalMapCard = ({
  terminal,
  terminalName,
  terminalColor,
  openEditTerminal,
  t,
  fullHeight,
}: {
  terminal: any;
  terminalName: string;
  terminalColor: string;
  openEditTerminal: () => void;
  t: any;
  fullHeight?: boolean;
}) => {
  const hasCoordinates =
    terminal?.latitude !== null &&
    terminal?.latitude !== undefined &&
    terminal?.longitude !== null &&
    terminal?.longitude !== undefined;

  const coordinates: [number, number] = hasCoordinates
    ? [Number(terminal.latitude), Number(terminal.longitude)]
    : [51.23102, 4.3979];

  return (
    <SectionCard
      sx={fullHeight ? { flex: 1, minHeight: 0, display: "flex", flexDirection: "column" } : undefined}
      contentSx={fullHeight ? { flex: 1, minHeight: 0, display: "flex", flexDirection: "column" } : undefined}
      title={getTranslatedLabel(t, "location", "Location")}
    >
      <Box
        sx={{
          height: fullHeight ? undefined : 230,
          flex: fullHeight ? 1 : undefined,
          minHeight: fullHeight ? 0 : undefined,
          borderRadius: 1,
          overflow: "hidden",
          border: `1px solid ${hexToRgba(terminalColor, 0.22)}`,
          "& .leaflet-container": {
            height: "100%",
            width: "100%",
          },
        }}
      >
        <MapContainer
          key={`${getEntityId(terminal)}-${coordinates[0]}-${coordinates[1]}`}
          center={coordinates}
          zoom={12}
          scrollWheelZoom={false}
          style={{
            height: "100%",
            width: "100%",
          }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          <Marker position={coordinates}>
            <Popup>{terminalName}</Popup>
          </Marker>
        </MapContainer>
      </Box>

    </SectionCard>
  );
};

const COUNTRY_OPTIONS = [
  { value: "Belgium", translationKey: "countryBelgium" },
  { value: "Netherlands", translationKey: "countryNetherlands" },
  { value: "Germany", translationKey: "countryGermany" },
  { value: "France", translationKey: "countryFrance" },
];

const InlineEditableField = ({
  label,
  value,
  displayValue,
  field,
  entity,
  onSave,
  type = "text",
  options,
  saveUrl,
  dense = false,
  hideLabel = false,
}: {
  label: string;
  value: any;
  displayValue?: React.ReactNode;
  field: string;
  entity?: any;
  onSave: (savedEntity?: any) => Promise<void> | void;
  type?:
    | "text"
    | "textarea"
    | "select"
    | "number"
    | "datetime-local"
    | "json";
  options?: { value: string; label: string }[];
  saveUrl?: string;
  dense?: boolean;
  hideLabel?: boolean;
}) => {
  const { t } = useTranslation();
  const { activeEditorId, setActiveEditorId } = useContext(InlineEditContext);
  const editorIdRef = useRef(
    `inline-editor-${field}-${Math.random().toString(36).slice(2)}`
  );
  const editorId = editorIdRef.current;
  const editing = activeEditorId === editorId;
  const getDraftValue = useCallback(
    (nextValue: any) => {
      if (type === "datetime-local") {
        return formatDateTimeInput(nextValue);
      }

      if (type === "json") {
        return nextValue ? JSON.stringify(nextValue, null, 2) : "";
      }

      return nextValue ?? "";
    },
    [type]
  );
  const [draft, setDraft] = useState(() => getDraftValue(value));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const resolvedSaveUrl = saveUrl || entity?.["@id"] || entity?.id;

  useEffect(() => {
    setDraft(getDraftValue(value));
  }, [getDraftValue, value]);

  useEffect(() => {
    if (!editing) {
      setDraft(getDraftValue(value));
      setSaveError("");
    }
  }, [editing, getDraftValue, value]);

  const handleSave = async () => {
    const originalDraft = getDraftValue(value);

    if (draft === originalDraft) {
      setActiveEditorId(null);
      return;
    }

    if (!resolvedSaveUrl) {
      setSaveError(t("itemCannotBeEdited"));
      return;
    }

    setSaving(true);
    setSaveError("");

    try {
      let savedValue: any = draft === "" ? null : draft;

      if (type === "number" && draft !== "") {
        savedValue = Number(draft);
      } else if (type === "datetime-local" && draft) {
        savedValue = new Date(draft).toISOString();
      } else if (type === "json" && draft) {
        savedValue = JSON.parse(draft);
      }

      const savedEntity = await patchEntity(
        resolvedSaveUrl === entity?.["@id"] || resolvedSaveUrl === entity?.id
          ? entity
          : { "@id": resolvedSaveUrl },
        { [field]: savedValue }
      );

      setActiveEditorId(null);
      await onSave(savedEntity);
    } catch (error) {
      setSaveError(
        error instanceof SyntaxError ? t("invalidJson") : t("saveFailed")
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setDraft(getDraftValue(value));
    setSaveError("");
    setActiveEditorId(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && type !== "textarea" && type !== "json") {
      e.preventDefault();
      handleSave();
    }
    if (e.key === "Escape") {
      handleCancel();
    }
  };

  return (
    <Box
      onClick={(event) => event.stopPropagation()}
      sx={{
        py: dense ? 0 : 1.1,
        width: "100%",
        borderBottom: dense ? "none" : "1px solid rgba(15, 23, 42, 0.08)",
        "&:last-of-type": { borderBottom: "none" },
        position: "relative",
        "&:hover .inline-edit-trigger": {
          backgroundColor: "rgba(25, 118, 210, 0.06)",
        },
        "&:hover .inline-edit-icon": {
          opacity: 1,
        },
      }}
    >
      {!hideLabel ? (
        <Typography
          sx={{
            fontSize: "0.75rem",
            color: "text.secondary",
            mb: 0.3,
          }}
        >
          {label}
        </Typography>
      ) : null}

      {editing ? (
        <>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {type === "select" ? (
            <FormControl size="small" fullWidth>
              <Select
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={handleKeyDown}
                autoFocus
                disabled={saving}
                sx={{ fontSize: "0.9rem" }}
              >
                {options?.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <TextField
              type={
                type === "number" || type === "datetime-local"
                  ? type
                  : undefined
              }
              size="small"
              fullWidth
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus
              disabled={saving}
              multiline={type === "textarea" || type === "json"}
              minRows={type === "textarea" ? 2 : type === "json" ? 5 : undefined}
              sx={{
                "& .MuiInputBase-input": { fontSize: "0.9rem", fontWeight: 600 },
              }}
              slotProps={{
                input: {
                  endAdornment: saving ? (
                    <InputAdornment position="end">
                      <CircularProgress size={16} />
                    </InputAdornment>
                  ) : undefined,
                },
              }}
            />
          )}

          <IconButton size="small" onClick={handleSave} disabled={saving} color="primary">
            <CheckCircleIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={handleCancel} disabled={saving}>
            <CancelOutlinedIcon fontSize="small" />
          </IconButton>
        </Box>
        {saveError ? (
          <Typography color="error" fontSize="0.7rem" mt={0.4}>
            {saveError}
          </Typography>
        ) : null}
        </>
      ) : (
        <Box
          className="inline-edit-trigger"
          onClick={() => setActiveEditorId(editorId)}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            cursor: "pointer",
            borderRadius: 0.5,
            mx: -0.5,
            px: 0.5,
            py: 0.3,
            transition: "background-color 0.12s ease",
          }}
        >
          <Typography
            sx={{
              fontSize: "0.9rem",
              color: value !== null && value !== undefined && value !== ""
                ? "text.primary"
                : "text.disabled",
              fontWeight: value !== null && value !== undefined && value !== ""
                ? 600
                : 400,
              wordBreak: "break-word",
              flex: 1,
              fontStyle:
                value !== null && value !== undefined && value !== ""
                  ? "normal"
                  : "italic",
              whiteSpace: type === "json" ? "pre-wrap" : undefined,
            }}
          >
            {displayValue ??
              (type === "json" && value
                ? JSON.stringify(value, null, 2)
                : value !== null && value !== undefined && value !== ""
                ? value
                : "—")}
          </Typography>

          <EditIcon
            className="inline-edit-icon"
            sx={{
              fontSize: 16,
              color: "primary.main",
              flexShrink: 0,
              opacity: 0,
              transition: "opacity 0.12s ease",
            }}
          />
        </Box>
      )}
    </Box>
  );
};

const InlineEntityAutocomplete = ({
  label,
  entity,
  field,
  value,
  options,
  onSave,
  multiple = false,
}: {
  label: string;
  entity: any;
  field: string;
  value: any;
  options: any[];
  onSave: (savedEntity?: any) => Promise<void> | void;
  multiple?: boolean;
}) => {
  const { t } = useTranslation();
  const { setActiveEditorId } = useContext(InlineEditContext);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const safeOptions = Array.isArray(options) ? options.filter(Boolean) : [];
  const collectionByField: Record<string, string> = {
    terminal: "terminals",
    vessel: "vessels",
    voyage: "voyages",
    labels: "labels",
  };
  const getOptionId = (option: any) =>
    typeof option === "string"
      ? option
      : option?.["@id"] ??
        (option?.id
          ? `/api/${collectionByField[field] || field}/${option.id}`
          : "");
  const selectedIds = new Set(
    (multiple ? (Array.isArray(value) ? value : []) : value ? [value] : [])
      .map(getOptionId)
      .filter(Boolean)
  );
  const selectedValue = multiple
    ? safeOptions.filter((option) => selectedIds.has(getOptionId(option)))
    : safeOptions.find((option) => selectedIds.has(getOptionId(option))) ?? null;

  const handleChange = async (_: any, nextValue: any) => {
    const payloadValue = multiple
      ? (Array.isArray(nextValue) ? nextValue : [])
          .map(getOptionId)
          .filter(Boolean)
      : nextValue
      ? getOptionId(nextValue)
      : null;

    setSaving(true);
    setSaveError("");

    try {
      const savedEntity = await patchEntity(entity, { [field]: payloadValue });
      await onSave(savedEntity);
    } catch {
      setSaveError(t("saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ py: 1.1, borderBottom: "1px solid rgba(15, 23, 42, 0.08)" }}>
      <Autocomplete
        multiple={multiple}
        size="small"
        options={safeOptions}
        value={selectedValue as any}
        disabled={saving}
        onOpen={() => setActiveEditorId(null)}
        onChange={handleChange}
        getOptionLabel={(option: any) =>
          option?.name ?? option?.label ?? option?.title ?? ""
        }
        isOptionEqualToValue={(option: any, selected: any) =>
          getOptionId(option) === getOptionId(selected)
        }
        renderOption={(props, option: any) => (
          <li {...props} key={getOptionId(option)}>
            {option?.color ? (
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  mr: 1,
                  borderRadius: "50%",
                  backgroundColor: option.color,
                  flexShrink: 0,
                }}
              />
            ) : null}
            {option?.name ?? option?.label ?? option?.title ?? ""}
          </li>
        )}
        renderTags={(selectedOptions: any[], getTagProps) =>
          selectedOptions.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={getOptionId(option)}
              size="small"
              label={option?.name ?? option?.label ?? option?.title ?? ""}
              sx={{
                backgroundColor: option?.color || undefined,
                color: option?.color
                  ? textColorHandler(option.color)
                  : undefined,
              }}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            error={Boolean(saveError)}
            helperText={saveError}
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {saving ? <CircularProgress size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
      />
    </Box>
  );
};

const TerminalCallDetailsDashboard: React.FC<
  TerminalCallDetailsDashboardProps
> = ({ terminalCall, containers, callback, callbackId, isChildModal }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState(TAB_OVERVIEW);

  const { voyage } = useSelector(selectedFilteredVoyage);
  const filteredTerminalCall = useSelector(selectedFilteredTerminalCall);
  const modal = useSelector((state: any) => state.modal);
  const selectedContainers = useSelector(
    (state: any) => state.selectedContainers
  );
  const relationOptions = useSelector((state: any) => ({
    labels: state.labels?.data || [],
    terminals: state.terminals?.data || [],
    vessels: state.vessels?.data || [],
    voyages: state.voyages?.data || [],
  }));

  const suppliedTerminalCall = terminalCall || filteredTerminalCall;
  const suppliedTerminalCallId = getEntityId(suppliedTerminalCall);
  const [dialogTerminalCall, setDialogTerminalCall] =
    useState<any>(suppliedTerminalCall);
  const [activeEditorId, setActiveEditorId] = useState<string | null>(null);
  const [uploadingFiles, setUploadingFiles] = useState<File[]>([]);
  const [fileUploadError, setFileUploadError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    setDialogTerminalCall(suppliedTerminalCall);
    setActiveEditorId(null);
  }, [suppliedTerminalCallId]);

  const activeTerminalCall = dialogTerminalCall || suppliedTerminalCall;
  const terminal = activeTerminalCall?.terminal;
  const vessel = activeTerminalCall?.vessel;

  const allContainers = useMemo(
    () =>
      Array.isArray(containers) && containers.length > 0
        ? containers
        : selectedContainers?.data || [],
    [containers, selectedContainers?.data]
  );

  const terminalColor = terminal?.color || DEFAULT_TERMINAL_COLOR;
  const terminalSoftBackground = hexToRgba(terminalColor, 0.12);
  const terminalHoverBackground = hexToRgba(terminalColor, 0.18);
  const terminalBorderBackground = hexToRgba(terminalColor, 0.28);

  const terminalName = `${
    terminal?.abbreviation ? `${terminal.abbreviation} - ` : ""
  }${terminal?.name || t("terminal")}`;

  const statusLabel =
    activeTerminalCall?.status
      ? t(activeTerminalCall.status.toLowerCase())
      : activeTerminalCall?.departure
      ? t("completed")
      : activeTerminalCall?.operationStart
      ? t("ongoing")
      : t("planned");

  const statusColor = activeTerminalCall?.departure
    ? "success"
    : activeTerminalCall?.operationStart
    ? "warning"
    : "success";

  const labels = Array.isArray(activeTerminalCall?.labels)
    ? activeTerminalCall.labels
    : [];

  const notes = Array.isArray(activeTerminalCall?.notes)
    ? activeTerminalCall.notes
    : [];

  const overviewNotes = useMemo(() => {
    const pinnedNotes = notes.filter((note: NoteItem) => Boolean(note.pinned));
    const unpinnedNotes = notes
      .filter((note: NoteItem) => !note.pinned)
      .sort((left: NoteItem, right: NoteItem) => {
        const leftDate = new Date(
          left.updatedAt || left.createdAt || 0
        ).getTime();
        const rightDate = new Date(
          right.updatedAt || right.createdAt || 0
        ).getTime();

        return rightDate - leftDate;
      });

    return [...pinnedNotes, ...unpinnedNotes.slice(0, 1)];
  }, [notes]);

  const hiddenOverviewNotesCount = Math.max(
    notes.filter((note: NoteItem) => !note.pinned).length - 1,
    0
  );

  const terminalContacts = Array.isArray(terminal?.contacts)
    ? terminal.contacts
    : [];

  const terminalLinks = Array.isArray(terminal?.links)
    ? terminal.links.filter((link: any) => link?.name?.trim() || link?.url?.trim())
    : [];

  const terminalFiles =
    terminal?.fileGroup?.files && Array.isArray(terminal.fileGroup.files)
      ? terminal.fileGroup.files
      : [];

  const terminalCallFiles =
    activeTerminalCall?.fileGroup?.files &&
    Array.isArray(activeTerminalCall.fileGroup.files)
      ? activeTerminalCall.fileGroup.files
      : [];

  const files = uniqueContainersById([
    ...terminalCallFiles,
    ...terminalFiles,
  ]);

  const containerById = useMemo(() => {
    return buildContainerLookup(allContainers || []);
  }, [allContainers]);

  const unloadingContainers = useMemo(() => {
    return (activeTerminalCall?.unloadingContainers || []).map((container: any) =>
      resolveContainerFromCollection(container, containerById)
    );
  }, [activeTerminalCall, containerById]);

  const loadingContainers = useMemo(() => {
    return (activeTerminalCall?.loadingContainers || []).map((container: any) =>
      resolveContainerFromCollection(container, containerById)
    );
  }, [activeTerminalCall, containerById]);

  const refreshSelectedTerminalCalls = useCallback(async () => {
    if (!voyage?.terminalCalls) {
      return;
    }

    await dispatch(
      getSelectedTerminalCalls(normalizeIds(voyage.terminalCalls)) as any
    );
  }, [dispatch, voyage]);

  const refreshCallback = useCallback(
    async (savedData?: any) => {
      await refreshSelectedTerminalCalls();

      if (callback) {
        await callback(savedData);
      }

      if (callbackId) {
        await runModalCallback(callbackId, savedData);
      }
    },
    [callback, callbackId, refreshSelectedTerminalCalls]
  );

  const handleTerminalFieldSaved = useCallback(async (savedEntity?: any) => {
    if (savedEntity) {
      setDialogTerminalCall((currentTerminalCall: any) => {
        if (!currentTerminalCall) {
          return currentTerminalCall;
        }

        if (
          getEntityId(savedEntity) === getEntityId(currentTerminalCall)
        ) {
          return {
            ...currentTerminalCall,
            ...savedEntity,
          };
        }

        if (
          getEntityId(savedEntity) ===
          getEntityId(currentTerminalCall?.terminal)
        ) {
          return {
            ...currentTerminalCall,
            terminal: {
              ...currentTerminalCall.terminal,
              ...savedEntity,
            },
          };
        }

        const savedNoteIndex = (currentTerminalCall.notes || []).findIndex(
          (note: any) => getEntityId(note) === getEntityId(savedEntity)
        );

        if (savedNoteIndex >= 0) {
          return {
            ...currentTerminalCall,
            notes: currentTerminalCall.notes.map((note: any, index: number) =>
              index === savedNoteIndex ? { ...note, ...savedEntity } : note
            ),
          };
        }

        if (
          normalizeApiId(savedEntity?.terminalCall) ===
          getEntityId(currentTerminalCall)
        ) {
          return {
            ...currentTerminalCall,
            notes: [...(currentTerminalCall.notes || []), savedEntity],
          };
        }

        return currentTerminalCall;
      });
    }

    await refreshCallback(savedEntity);
  }, [refreshCallback]);

  const handleFileDrop = useCallback(
    async (droppedFiles: File[]) => {
      if (!droppedFiles.length || !activeTerminalCall) {
        return;
      }

      setUploadingFiles(droppedFiles);
      setFileUploadError(null);

      try {
        const uploadedFiles = await Promise.all(
          droppedFiles.map(uploadFileEntity)
        );
        const nextTerminalCallFiles = uniqueContainersById([
          ...terminalCallFiles,
          ...uploadedFiles,
        ]);
        const fileIds = nextTerminalCallFiles
          .map((file: any) => getEntityId(file))
          .filter(Boolean);
        const currentFileGroup = activeTerminalCall.fileGroup;
        const savedFileGroup = currentFileGroup
          ? await patchEntity(currentFileGroup, { files: fileIds })
          : await createEntity("file_groups", { files: fileIds });

        if (currentFileGroup) {
          setDialogTerminalCall((currentTerminalCall: any) => ({
            ...currentTerminalCall,
            fileGroup: savedFileGroup,
          }));
          await refreshCallback(savedFileGroup);
        } else {
          const savedTerminalCall = await patchEntity(activeTerminalCall, {
            fileGroup: getEntityId(savedFileGroup),
          });
          await handleTerminalFieldSaved(savedTerminalCall);
        }
      } catch (error: any) {
        setFileUploadError(
          error?.message || getTranslatedLabel(t, "fileUploadFailed", "File upload failed.")
        );
      } finally {
        setUploadingFiles([]);
      }
    },
    [
      activeTerminalCall,
      handleTerminalFieldSaved,
      refreshCallback,
      t,
      terminalCallFiles,
    ]
  );

  const handleBooleanFieldChange = useCallback(
    async (entity: any, field: string, checked: boolean) => {
      const savedEntity = await patchEntity(entity, { [field]: checked });
      await handleTerminalFieldSaved(savedEntity);
    },
    [handleTerminalFieldSaved]
  );

  const handleCreateNote = useCallback(
    async (values: NoteValues) => {
      const createdNote = await createEntity("notes", {
        ...values,
        terminalCall: getEntityId(activeTerminalCall),
      });

      await handleTerminalFieldSaved(createdNote);
    },
    [activeTerminalCall, handleTerminalFieldSaved]
  );

  const handleUpdateNote = useCallback(
    async (note: NoteItem, values: NoteValues) => {
      const savedNote = await patchEntity(note, values);
      await handleTerminalFieldSaved(savedNote);
    },
    [handleTerminalFieldSaved]
  );

  const handleDeleteNote = useCallback(
    async (note: NoteItem) => {
      await deleteEntity(note);

      setDialogTerminalCall((currentTerminalCall: any) => ({
        ...currentTerminalCall,
        notes: (currentTerminalCall?.notes || []).filter(
          (currentNote: any) =>
            getEntityId(currentNote) !== getEntityId(note)
        ),
      }));

      await refreshCallback();
    },
    [refreshCallback]
  );

  useEffect(() => {
    dispatch(getLabels() as any);
    dispatch(getTerminals() as any);
    dispatch(getVessels() as any);
    dispatch(getVoyages() as any);
  }, [dispatch]);

  useEffect(() => {
    const handleTerminalSaved = async (event: Event) => {
      await handleTerminalFieldSaved((event as CustomEvent)?.detail);
    };

    window.addEventListener("terminal-crud:saved", handleTerminalSaved);

    return () => {
      window.removeEventListener("terminal-crud:saved", handleTerminalSaved);
    };
  }, [handleTerminalFieldSaved]);

  const openChildModal = useCallback(
    (opts: any) => dispatch(showChildModal(opts) as any),
    [dispatch]
  );

  const handleCloseForm = () => {
    if (isChildModal || modal.childModal) {
      dispatch(hideChildModal() as any);
      return;
    }

    dispatch(hideModal() as any);
  };

  const handleDeleteTerminalCall = useCallback(async () => {
    setDeleteDialogOpen(false);
    await dispatch(removeTerminalCall(activeTerminalCall) as any);
    handleCloseForm();
    if (typeof refreshCallback === "function") {
      refreshCallback();
    }
  }, [dispatch, activeTerminalCall, refreshCallback]);

  const openEditTerminalCall = () => {
    openChildModal({
      title: t("editPlanning") || t("editTerminalCall"),
      description: getTranslatedLabel(
        t,
        "modifyPlanningDescription",
        "Modify the planning details."
      ),
      form: "terminalCall",
      formProps: {
        terminalCall: activeTerminalCall,
        callback: refreshCallback,
        isChildModal: true,
      },
      size: "default",
    });
  };

  const openEditTerminal = () => {
    openChildModal({
      title: t("editTerminal"),
      description: t("modifyTerminalDescription"),
      form: "terminal",
      formProps: {
        terminal,
        callback: refreshCallback,
        onClose: () => {
          dispatch(hideChildModal() as any);
        },
        isChildModal: true,
      },
      size: "default",
    });
  };

  const openFilePreviewModal = (file: any) => {
    openChildModal({
      title: t("viewFile"),
      description: getFileName(file),
      form: "fileview",
      formProps: {
        file,
        isChildModal: true,
      },
      size: "default",
    });
  };

  const openLink = (url?: string | null) => {
    const normalizedUrl = normalizeLinkUrl(url);

    if (!normalizedUrl) {
      return;
    }

    window.open(normalizedUrl, "_blank", "noopener,noreferrer");
  };

  if (!activeTerminalCall) {
    return <Alert severity="warning">{t("selectPlanning")}</Alert>;
  }

  return (
    <InlineEditContext.Provider
      value={{ activeEditorId, setActiveEditorId }}
    >
    <Box
      sx={{
        backgroundColor: "#ffffff",
        height: MODAL_FIXED_HEIGHT,
        minHeight: MODAL_FIXED_HEIGHT,
        maxHeight: MODAL_FIXED_HEIGHT,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={2}
          sx={{ mb: 2, flexShrink: 0 }}
        >
          <Stack direction="row" spacing={2} alignItems="center" minWidth={0}>
            <Box
              sx={{
                width: 62,
                height: 62,
                borderRadius: 2,
                backgroundColor: terminalSoftBackground,
                color: terminalColor,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <DirectionsBoatIcon sx={{ fontSize: 38 }} />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
                <Typography
                  variant="h5"
                  fontWeight={950}
                  noWrap
                  sx={{ minWidth: 0 }}
                >
                  {terminalName}
                </Typography>

                <Chip
                  size="small"
                  color={statusColor as any}
                  label={statusLabel}
                  sx={{ fontWeight: 800 }}
                />
              </Stack>

              <Typography color="text.secondary" mt={0.5}>
                {t("city")}: {terminal?.city || "—"}
                {terminal?.country ? `, ${terminal.country}` : ""}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={openEditTerminalCall}
              sx={{ textTransform: "none" }}
            >
              {getTranslatedLabel(t, "edit", "Edit")}
            </Button>

            {/* <Button
              variant="outlined"
              startIcon={<MoreVertIcon />}
              sx={{ textTransform: "none" }}
            >
              {getTranslatedLabel(t, "more", "More")}
            </Button> */}
          </Stack>
        </Stack>

        <Tabs
          value={activeTab}
          onChange={(_, nextValue) => setActiveTab(nextValue)}
          sx={{
              mb: 2,
              borderBottom: "1px solid rgba(15, 23, 42, 0.1)",
              minHeight: 42,
              flexShrink: 0,
              "& .MuiTab-root": {
              minHeight: 42,
              textTransform: "none",
              fontWeight: 700,
           },
          }}
        >
          <Tab icon={<HomeIcon />} iconPosition="start" label={t("overview")} />
          <Tab
            icon={<LocalShippingIcon />}
            iconPosition="start"
            label={t("cargo")}
          />
          <Tab icon={<FolderIcon />} iconPosition="start" label={t("files")} />
          <Tab icon={<MapIcon />} iconPosition="start" label={t("map")} />
          <Tab icon={<NotesIcon />} iconPosition="start" label={t("notes")} />
          <Tab
            icon={<TerminalIcon />}
            iconPosition="start"
            label={getTranslatedLabel(t, "terminalInfo", "Terminal Info")}
          />
          <Tab
            icon={<SettingsIcon />}
            iconPosition="start"
            label={getTranslatedLabel(t, "debugging", "Debugging")}
          />
        </Tabs>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflowY: "auto",
            overflowX: "hidden",
            pr: 0.75,
            pb: 2,
            scrollbarWidth: "thin",
            scrollbarColor: "rgba(15, 23, 42, 0.28) transparent",
            "&::-webkit-scrollbar": {
              width: 6,
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(15, 23, 42, 0.25)",
              borderRadius: 999,
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: "rgba(15, 23, 42, 0.38)",
            },
          }}
        >
        {activeTab === TAB_OVERVIEW ? (
          <Grid container spacing={2} alignItems="stretch" sx={{ minHeight: 0, flex: 1 }}>
            <Grid item xs={12} md={2.5} sx={{ display: "flex" }}>
              <Stack spacing={2} sx={{ flex: 1 }}>
                <SectionCard
                  title={getTranslatedLabel(
                    t,
                    "journeyAndTransport",
                    "Journey & Transport"
                  )}
                >
                  <InlineEntityAutocomplete
                    label={getTranslatedLabel(t, "voyage", "Voyage")}
                    entity={activeTerminalCall}
                    field="voyage"
                    value={activeTerminalCall?.voyage || voyage}
                    options={relationOptions.voyages}
                    onSave={handleTerminalFieldSaved}
                  />

                  <InlineEntityAutocomplete
                    label={getTranslatedLabel(t, "vessel", "Vessel")}
                    entity={activeTerminalCall}
                    field="vessel"
                    value={vessel}
                    options={relationOptions.vessels}
                    onSave={handleTerminalFieldSaved}
                  />

                  <InlineEntityAutocomplete
                    label={t("terminal")}
                    entity={activeTerminalCall}
                    field="terminal"
                    value={terminal}
                    options={relationOptions.terminals}
                    onSave={handleTerminalFieldSaved}
                  />
                </SectionCard>

                <SectionCard
                  title={getTranslatedLabel(t, "operation", "Operation")}
                >
                  <Stack spacing={1.5}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      spacing={1}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <LockIcon fontSize="small" color="action" />

                        <Typography fontSize="0.86rem">
                          {getTranslatedLabel(
                            t,
                            "lockLoadingPlan",
                            "Lock loading plan"
                          )}
                        </Typography>

                        <InfoIcon fontSize="small" color="primary" />
                      </Stack>

                      <Checkbox
                        checked={Boolean(activeTerminalCall?.loadingPlanLocked)}
                        onChange={(_, checked) =>
                          handleBooleanFieldChange(
                            activeTerminalCall,
                            "loadingPlanLocked",
                            checked
                          )
                        }
                      />
                    </Stack>

                    <InlineEditableField
                      label={getTranslatedLabel(t, "status", "Status")}
                      value={activeTerminalCall?.status || ""}
                      field="status"
                      entity={activeTerminalCall}
                      onSave={handleTerminalFieldSaved}
                      type="select"
                      options={[
                        { value: "Planned", label: t("planned") },
                        { value: "Ongoing", label: t("ongoing") },
                        { value: "Completed", label: t("completed") },
                      ]}
                    />

                    <InlineEditableField
                      label={getTranslatedLabel(t, "dockingSide", "Docking side")}
                      value={
                        activeTerminalCall?.dockingSide || ""
                      }
                      displayValue={
                        activeTerminalCall?.dockingSide
                          ? t(activeTerminalCall.dockingSide)
                          : "—"
                      }
                      field="dockingSide"
                      entity={activeTerminalCall}
                      onSave={handleTerminalFieldSaved}
                      type="select"
                      options={[
                        { value: "port", label: t("port") },
                        { value: "starboard", label: t("starboard") },
                      ]}
                    />

                    <InlineEditableField
                      label={getTranslatedLabel(t, "waterType", "Water type")}
                      value={activeTerminalCall?.waterType || ""}
                      displayValue={
                        activeTerminalCall?.waterType
                          ? t(activeTerminalCall.waterType)
                          : "—"
                      }
                      field="waterType"
                      entity={activeTerminalCall}
                      onSave={handleTerminalFieldSaved}
                      type="select"
                      options={[
                        { value: "fresh", label: t("fresh") || "Fresh" },
                        { value: "salt", label: t("salt") || "Salt" },
                      ]}
                    />

                    <InlineEditableField
                      label={getTranslatedLabel(
                        t,
                        "containerStowageType",
                        "Container stowage type"
                      )}
                      value={
                        activeTerminalCall?.containerStowageType || ""
                      }
                      displayValue={
                        activeTerminalCall?.containerStowageType
                          ? t(activeTerminalCall.containerStowageType)
                          : "—"
                      }
                      field="containerStowageType"
                      entity={activeTerminalCall}
                      onSave={handleTerminalFieldSaved}
                      type="select"
                      options={[
                        { value: "secured", label: t("secured") || "Secured" },
                        {
                          value: "unsecured",
                          label: t("unsecured") || "Unsecured",
                        },
                      ]}
                    />
                  </Stack>
                </SectionCard>

                <SectionCard title={getTranslatedLabel(t, "labels", "Labels")}>
                  <InlineEntityAutocomplete
                    label={getTranslatedLabel(t, "labels", "Labels")}
                    entity={activeTerminalCall}
                    field="labels"
                    value={labels}
                    options={relationOptions.labels}
                    onSave={handleTerminalFieldSaved}
                    multiple
                  />
                </SectionCard>
              </Stack>
            </Grid>

            <Grid item xs={12} md={6.5} sx={{ display: "flex", flexDirection: "column" }}>
              <Box sx={{ flexShrink: 0 }}>
                <TerminalCallTimeline
                  terminalCall={activeTerminalCall}
                  t={t}
                  onSave={handleTerminalFieldSaved}
                />
              </Box>

              <Box sx={{ mt: 2, flex: 1, minHeight: 0 }}>
                <Grid container spacing={2} sx={{ height: "100%" }}>
                  <Grid item xs={12} md={7.2} sx={{ display: "flex" }}>
                    <Box sx={{ width: "100%" }}>
                      <TerminalCallOverviewStats
                        terminalCall={activeTerminalCall}
                        allContainers={allContainers}
                        t={t}
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={4.8} sx={{ display: "flex" }}>
                    <NotesPanel
                    title={`${t("notes")} (${t("terminalCall")})`}
                    notes={overviewNotes}
                    labelOptions={relationOptions.labels}
                    emptyText={t("noNotes")}
                    addLabel={getTranslatedLabel(t, "addNote", "Add note")}
                    noteLabel={getTranslatedLabel(t, "note", "Note")}
                    createLabel={getTranslatedLabel(t, "save", "Save")}
                    updateLabel={getTranslatedLabel(t, "save", "Save")}
                    cancelLabel={getTranslatedLabel(t, "cancel", "Cancel")}
                    deleteLabel={getTranslatedLabel(t, "delete", "Delete")}
                    editLabel={getTranslatedLabel(t, "edit", "Edit")}
                    showMoreLabel={t("showMoreNotes", {
                      count: hiddenOverviewNotesCount,
                    })}
                    showMoreCount={hiddenOverviewNotesCount}
                    onShowMore={() => {
                      setActiveEditorId(null);
                      setActiveTab(TAB_NOTES);
                    }}
                    onCreate={handleCreateNote}
                    onUpdate={handleUpdateNote}
                    onDelete={handleDeleteNote}
                  />
                </Grid>
              </Grid>
              </Box>
            </Grid>

            <Grid item xs={12} md={3} sx={{ display: "flex" }}>
              <Stack spacing={2} sx={{ flex: 1 }}>
                <TerminalMapCard
                  terminal={terminal}
                  terminalName={terminalName}
                  terminalColor={terminalColor}
                  openEditTerminal={openEditTerminal}
                  t={t}
                />

                <SectionCard
                  title={getTranslatedLabel(
                    t,
                    "terminalContacts",
                    "Terminal contacts"
                  )}
                >
                  {terminalContacts.length > 0 ? (
                    <Stack spacing={1.5}>
                      {terminalContacts.map((contact: any, index: number) => (
                        <Table key={`contact-${index}`} size="small">
                          <TableBody>
                            <TableRow>
                              <TableCell
                                sx={{
                                  width: 90,
                                  borderBottom: "none",
                                  color: "text.secondary",
                                  py: 0.35,
                                }}
                              >
                                {t("name")}
                              </TableCell>

                              <TableCell
                                sx={{ borderBottom: "none", py: 0.35 }}
                              >
                                {contact.name || "—"}
                              </TableCell>
                            </TableRow>

                            <TableRow>
                              <TableCell
                                sx={{
                                  width: 90,
                                  borderBottom: "none",
                                  color: "text.secondary",
                                  py: 0.35,
                                }}
                              >
                                {t("phone")}
                              </TableCell>

                              <TableCell
                                sx={{ borderBottom: "none", py: 0.35 }}
                              >
                                {contact.phoneNumber || contact.phone || "—"}
                              </TableCell>
                            </TableRow>

                            <TableRow>
                              <TableCell
                                sx={{
                                  width: 90,
                                  borderBottom: "none",
                                  color: "text.secondary",
                                  py: 0.35,
                                }}
                              >
                                {t("email")}
                              </TableCell>

                              <TableCell
                                sx={{ borderBottom: "none", py: 0.35 }}
                              >
                                {contact.email || "—"}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      ))}
                    </Stack>
                  ) : (
                    <Alert severity="info">{t("noContactsForTerminal")}</Alert>
                  )}
                </SectionCard>

                <SectionCard
                  sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
                  contentSx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
                  title={getTranslatedLabel(
                    t,
                    "terminalFiles",
                    "Terminal files"
                  )}
                >
                  <FileDropzoneGallery
                    files={files}
                    uploadingFiles={uploadingFiles}
                    uploadError={fileUploadError}
                    onDrop={handleFileDrop}
                    onPreview={openFilePreviewModal}
                    compact
                    t={t}
                    sx={{ flex: 1, minHeight: 0 }}
                  />

                  {terminalLinks.length > 0 ? (
                    <>
                      <Divider sx={{ my: 1.5 }} />

                      <Box>
                        <Typography
                          sx={{
                            mb: 1,
                            fontSize: "0.82rem",
                            fontWeight: 900,
                            color: "text.primary",
                          }}
                        >
                          {getTranslatedLabel(
                            t,
                            "terminalUrls",
                            "Terminal URL's"
                          )}
                        </Typography>

                        <TerminalUrlPills
                          links={terminalLinks}
                          terminalColor={terminalColor}
                          terminalSoftBackground={terminalSoftBackground}
                          terminalHoverBackground={terminalHoverBackground}
                          terminalBorderBackground={terminalBorderBackground}
                          openLink={openLink}
                          t={t}
                        />
                      </Box>
                    </>
                  ) : null}

                </SectionCard>
              </Stack>
            </Grid>
          </Grid>
        ) : null}

        {activeTab === TAB_CARGO ? (
          <TerminalCallCargoTab
            loadingContainers={loadingContainers}
            unloadingContainers={unloadingContainers}
            refreshCallback={refreshCallback}
            t={t}
          />
        ) : null}

        {activeTab === TAB_FILES ? (
          <SectionCard title={getTranslatedLabel(t, "files", "Files")}>
            <FileDropzoneGallery
              files={files}
              uploadingFiles={uploadingFiles}
              uploadError={fileUploadError}
              onDrop={handleFileDrop}
              onPreview={openFilePreviewModal}
              t={t}
            />
          </SectionCard>
        ) : null}

        {activeTab === TAB_MAP ? (
          <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            <TerminalMapCard
              terminal={terminal}
              terminalName={terminalName}
              terminalColor={terminalColor}
              openEditTerminal={openEditTerminal}
              t={t}
              fullHeight
            />
          </Box>
        ) : null}

        {activeTab === TAB_NOTES ? (
          <NotesPanel
            title={getTranslatedLabel(t, "notes", "Notes")}
            notes={notes}
            labelOptions={relationOptions.labels}
            emptyText={t("noNotes")}
            addLabel={getTranslatedLabel(t, "addNote", "Add note")}
            noteLabel={getTranslatedLabel(t, "note", "Note")}
            createLabel={getTranslatedLabel(t, "save", "Save")}
            updateLabel={getTranslatedLabel(t, "save", "Save")}
            cancelLabel={getTranslatedLabel(t, "cancel", "Cancel")}
            deleteLabel={getTranslatedLabel(t, "delete", "Delete")}
            editLabel={getTranslatedLabel(t, "edit", "Edit")}
            onCreate={handleCreateNote}
            onUpdate={handleUpdateNote}
            onDelete={handleDeleteNote}
          />
        ) : null}

        {activeTab === TAB_TERMINAL_INFO ? (
          <TerminalInfoForm
            terminal={terminal}
            terminalContacts={terminalContacts}
            terminalLinks={terminalLinks}
            t={t}
            relationOptions={relationOptions}
            openEditTerminal={openEditTerminal}
            handleTerminalFieldSaved={handleTerminalFieldSaved}
          />
        ) : null}

        {activeTab === TAB_DEBUGGING ? (
          <TerminalCallDebugStates terminalCall={activeTerminalCall} t={t} />
        ) : null}
        </Box>
      </Box>

      <Grid
        container
        className="button-wrapper"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          minHeight: MODAL_FOOTER_HEIGHT,
          flexShrink: 0,
          pt: 1,
          borderTop: "1px solid rgba(15, 23, 42, 0.08)",
          backgroundColor: "#ffffff",
        }}
      >
        <Stack direction="row" spacing={1}>
          <Button color="error" variant="contained" size="small" onClick={() => setDeleteDialogOpen(true)}>
            <DeleteIcon fontSize="inherit" />
            &nbsp;{getTranslatedLabel(t, "delete", "Delete")}
          </Button>
        </Stack>

        <Button
          color="primary"
          variant="contained"
          size="small"
          onClick={handleCloseForm}
        >
          <CloseIcon fontSize="inherit" />
          &nbsp;{t("close")}
        </Button>
      </Grid>
    </Box>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>
          {getTranslatedLabel(t, "confirmDelete", "Confirm deletion")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {getTranslatedLabel(t, "confirmDeleteTerminalCall", "Are you sure you want to delete this terminal call? This action cannot be undone.")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            {t("cancel")}
          </Button>
          <Button onClick={handleDeleteTerminalCall} color="error" variant="contained" startIcon={<DeleteIcon />}>
            {getTranslatedLabel(t, "delete", "Delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </InlineEditContext.Provider>
  );
};

const TerminalInfoForm = ({
  terminal,
  terminalContacts,
  terminalLinks,
  t,
  relationOptions,
  openEditTerminal,
  handleTerminalFieldSaved,
}: {
  terminal: any;
  terminalContacts: any[];
  terminalLinks: any[];
  t: any;
  relationOptions: any;
  openEditTerminal: () => void;
  handleTerminalFieldSaved: (savedEntity?: any) => void;
}) => {
  const [contactsDraft, setContactsDraft] = useState<any[]>(() =>
    (terminalContacts || []).map(normalizeContactForForm)
  );
  const [aliasesDraft, setAliasesDraft] = useState<string[]>(
    () => terminal?.aliases || []
  );
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    setContactsDraft((terminalContacts || []).map(normalizeContactForForm));
    setAliasesDraft(terminal?.aliases || []);
  }, [terminalContacts, terminal?.aliases]);

  const handleContactChange = (index: number, field: string, value: string) => {
    setContactsDraft((prev: any[]) =>
      prev.map((contact: any, i: number) =>
        i === index ? { ...contact, [field]: value } : contact
      )
    );
  };

  const handleAddContact = () => {
    setContactsDraft((prev: any[]) => [...prev, getDefaultContact()]);
  };

  const handleRemoveContact = (index: number) => {
    setContactsDraft((prev: any[]) => prev.filter((_: any, i: number) => i !== index));
  };

  const handleAddAlias = () => {
    setAliasesDraft((prev: string[]) => [...prev, ""]);
  };

  const handleAliasChange = (index: number, value: string) => {
    setAliasesDraft((prev: string[]) =>
      prev.map((alias: string, i: number) => (i === index ? value : alias))
    );
  };

  const handleRemoveAlias = (index: number) => {
    setAliasesDraft((prev: string[]) => prev.filter((_: string, i: number) => i !== index));
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);

    const normalizedContacts = normalizeContactsForSubmit(contactsDraft);
    const normalizedAliases = aliasesDraft
      .map((a: string) => a.trim())
      .filter(Boolean);

    try {
      const savedTerminal = await patchEntity(terminal, {
        contacts: normalizedContacts,
        aliases: normalizedAliases,
      });
      handleTerminalFieldSaved(savedTerminal);
    } catch (err: any) {
      setSaveError(err?.message || "Failed to save contacts and aliases");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <SectionCard
          title={getTranslatedLabel(t, "terminalInfo", "Terminal Info")}
          action={
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={openEditTerminal}
              sx={{ textTransform: "none" }}
            >
              {getTranslatedLabel(t, "edit", "Edit")}
            </Button>
          }
        >
          <InlineEditableField
            label={t("name")}
            value={terminal?.name || ""}
            field="name"
            entity={terminal}
            onSave={handleTerminalFieldSaved}
          />
          <InlineEditableField
            label={t("abbreviation")}
            value={terminal?.abbreviation || ""}
            field="abbreviation"
            entity={terminal}
            onSave={handleTerminalFieldSaved}
          />
          <InlineEditableField
            label={t("city")}
            value={terminal?.city || ""}
            field="city"
            entity={terminal}
            onSave={handleTerminalFieldSaved}
          />
          <InlineEditableField
            label={getTranslatedLabel(t, "latitude", "Latitude")}
            value={terminal?.latitude ?? ""}
            field="latitude"
            entity={terminal}
            onSave={handleTerminalFieldSaved}
            type="number"
          />
          <InlineEditableField
            label={getTranslatedLabel(t, "longitude", "Longitude")}
            value={terminal?.longitude ?? ""}
            field="longitude"
            entity={terminal}
            onSave={handleTerminalFieldSaved}
            type="number"
          />
          <InlineEditableField
            label={t("country")}
            value={terminal?.country || ""}
            field="country"
            entity={terminal}
            onSave={handleTerminalFieldSaved}
            type="select"
            options={COUNTRY_OPTIONS.map((option: any) => ({
              value: option.value,
              label: t(option.translationKey),
            }))}
          />
          <InlineEditableField
            label={t("description")}
            value={terminal?.description || ""}
            field="description"
            entity={terminal}
            onSave={handleTerminalFieldSaved}
            type="textarea"
          />
          <InlineEditableField
            label={getTranslatedLabel(t, "color", "Color")}
            value={terminal?.color || ""}
            field="color"
            entity={terminal}
            onSave={handleTerminalFieldSaved}
          />
          <InlineEntityAutocomplete
            label={getTranslatedLabel(t, "labels", "Labels")}
            entity={terminal}
            field="labels"
            value={terminal?.labels || []}
            options={relationOptions.labels}
            onSave={handleTerminalFieldSaved}
            multiple
          />
        </SectionCard>
      </Grid>

      <Grid item xs={12} md={6}>
        <Stack spacing={2}>
          {saveError ? (
            <Alert severity="error" onClose={() => setSaveError(null)}>
              {saveError}
            </Alert>
          ) : null}

          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
            <Grid
              container
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1 }}
            >
              <Grid item>
                <Typography variant="h6" className="form-fields-title">
                  {getTranslatedLabel(t, "terminalContacts", "Terminal contacts")}
                </Typography>
              </Grid>

              <Grid item>
                <Button
                  type="button"
                  size="small"
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddContact}
                  disabled={saving}
                >
                  {t("addFormTitleByType", { type: t("contact") })}
                </Button>
              </Grid>
            </Grid>

            <Box
              sx={{
                maxHeight: { xs: 260, md: 300 },
                overflow: "auto",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1.5,
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: "30%" }}>{t("name")}</TableCell>
                    <TableCell sx={{ width: "25%" }}>{t("phone")}</TableCell>
                    <TableCell sx={{ width: "35%" }}>{t("email")}</TableCell>
                    <TableCell align="right" sx={{ width: 90 }}>
                      {t("actions")}
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {contactsDraft.map((contact: any, index: number) => {
                    const emailError =
                      contact.email &&
                      !isValidContactEmail(contact.email)
                        ? t("invalidEmailFormat")
                        : undefined;

                    return (
                      <TableRow key={`contact-${index}`}>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            placeholder={t("name")}
                            value={contact.name}
                            onChange={(e) =>
                              handleContactChange(index, "name", e.target.value)
                            }
                            disabled={saving}
                          />
                        </TableCell>

                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            placeholder={t("phone")}
                            value={contact.phoneNumber}
                            onChange={(e) =>
                              handleContactChange(index, "phoneNumber", e.target.value)
                            }
                            disabled={saving}
                          />
                        </TableCell>

                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            type="email"
                            placeholder={t("email")}
                            value={contact.email}
                            onChange={(e) =>
                              handleContactChange(index, "email", e.target.value)
                            }
                            disabled={saving}
                            error={Boolean(emailError)}
                            helperText={emailError}
                          />
                        </TableCell>

                        <TableCell align="right">
                          <IconButton
                            type="button"
                            size="small"
                            color="error"
                            disabled={saving}
                            onClick={() => handleRemoveContact(index)}
                          >
                            <DeleteIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {contactsDraft.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          textAlign="center"
                          sx={{ py: 2 }}
                        >
                          {t("noContactsForTerminal")}
                        </Typography>

                        <Box display="flex" justifyContent="center" pb={1}>
                          <Button
                            type="button"
                            size="small"
                            startIcon={<AddIcon />}
                            disabled={saving}
                            onClick={handleAddContact}
                          >
                            {t("addFormTitleByType", { type: t("contact") })}
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </Box>
          </Paper>

          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
            <Grid
              container
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1 }}
            >
              <Grid item>
                <Typography variant="h6" className="form-fields-title">
                  {t("aliases")}
                </Typography>
              </Grid>

              <Grid item>
                <Button
                  type="button"
                  size="small"
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddAlias}
                  disabled={saving}
                >
                  {t("addAlias")}
                </Button>
              </Grid>
            </Grid>

            <Box
              sx={{
                maxHeight: { xs: 220, md: 260 },
                overflow: "auto",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1.5,
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>{t("alias")}</TableCell>
                    <TableCell align="right" sx={{ width: 90 }}>
                      {t("actions")}
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {aliasesDraft.map((alias: string, index: number) => (
                    <TableRow key={`alias-${index}`}>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder={t("alias")}
                          value={alias}
                          onChange={(e) =>
                            handleAliasChange(index, e.target.value)
                          }
                          disabled={saving}
                        />
                      </TableCell>

                      <TableCell align="right">
                        <IconButton
                          type="button"
                          size="small"
                          color="error"
                          disabled={saving}
                          onClick={() => handleRemoveAlias(index)}
                        >
                          <DeleteIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}

                  {aliasesDraft.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={2}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          textAlign="center"
                          sx={{ py: 2 }}
                        >
                          {t("noAliasesForTerminal")}
                        </Typography>

                        <Box display="flex" justifyContent="center" pb={1}>
                          <Button
                            type="button"
                            size="small"
                            startIcon={<AddIcon />}
                            disabled={saving}
                            onClick={handleAddAlias}
                          >
                            {t("addAlias")}
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </Box>
          </Paper>

          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={15} /> : <SaveIcon />}
            >
              {saving ? t("saving") : t("save")}
            </Button>
          </Box>
        </Stack>
      </Grid>


    </Grid>
  );
};

export default TerminalCallDetailsDashboard;
